import leadContactIcon from "../assets/leads-table/contact.svg";
import leadFilterIcon from "../assets/leads-table/filter.svg";
import leadImportIcon from "../assets/leads-table/import.svg";
import leadAddIcon from "../assets/leads-table/add-lead.svg";
import leadAgentIcon from "../assets/leads-table/agent.svg";
import leadSearchIcon from "../assets/leads-table/search.svg";
import { loadFilterRecords } from "../utils/filterRecords";
import ContactFiltersDrawer from "../components/ContactFiltersDrawer";
import { buildContactListQuery, contactFilterPills } from "../utils/contactListQuery";
import { getSalesStatusColors, getSalesStatusChipSx, SALES_STATUS_DISABLED } from "../utils/salesStatusColors";
import { notifyCompanyRelationshipsChanged } from "../utils/companyRelationshipCounts";
import CompanyTableCell from "../components/CompanyTableCell";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { LEAD_EXPORT_TAB_FIELDS } from "../utils/exportTabFields";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  TableCell,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import useAuthController from "../hooks/useAuthController";
import useLeadRouteStat from "../hooks/useLeadRouteStat";
import Breadcrumb from "../components/Breadcrumb";
import GenericDataTable from "../components/GenericDataTable";
import SharedBulkActionsBar from "../components/BulkActionsBar";
import CsvImportDialog from "../components/CsvImportDialog";
import useCsvImport from "../hooks/useCsvImport";
import StatsSlider from "../components/StatsSlider";
import TopListHeaderBar from "../components/TopListHeaderBar";
import TabNavigationBar from "../components/TabNavigationBar";
import CompanyFiltersDrawer, {
  DEFAULT_FILTERS,
} from "../components/CompanyFiltersDrawer";
import CompanyAdvancedSearchView from "../components/CompanyAdvancedSearchView";
import LeadDetailsView from "../components/LeadDetailsView";
import CompanyProfileDrawer from "../components/CompanyProfileDrawer";
import LeadProfileDrawer from "../components/LeadProfileDrawer";
import LeadDescriptionDialog from "../components/PopupWindows/LeadDescriptionDialog";
import AddToSequenceDialog from "../components/PopupWindows/AddToSequenceDialog.jsx";
import AIAgentDialog from "../components/PopupWindows/AIAgentDialog.jsx";
import AssignTeamMemberAction from "../components/AssignTeamMemberAction";
import { ProposalActionIcon } from "../components/ActionButtonIcons";
import InfoActionIcons from "../components/InfoActionIcons";
import HiringManagerProfileDrawer from "../components/HiringManagerProfileDrawer";
import ListTableTabsTemplate from "../components/layouts/ListTableTabsTemplate";
import {
  buildHiringManagerSequenceItem,
  buildLeadSequenceItem,
} from "../utils/sequenceItems";
import {
  createSidebarResetState,
  getSidebarResetToken,
} from "../utils/sidebarNavigation";
import { areSamePersonNames } from "../utils/recruiterOwnership";
import { formatRelativeTimeShort, toTimestampValue } from "../utils/relativeTime";
import {
  getEntityLastActivityType,
  getEntityLastActivityValue,
  getEntityLastSyncValue,
} from "../utils/entityActivityTimestamps";
import {
  normalizeHiringManagerEntityType,
} from "../utils/hiringManagers";
import { normalizeContactRoleLabel } from "../utils/contactRoles";
import { COMPANY_PROFILE_TABS, findTabLabel } from "../utils/breadcrumbs";
import {
  buildLocationOptions,
  extractRowLocations,
  matchesLocationFilters,
  normalizeLocationFilterValues,
} from "../utils/locationFilters";
import { buildDetailFieldOptions } from "../utils/detailFieldOptions";
import { buildOpenCompanyNavPayload } from "../utils/companyNavigation";
import { normalizeContactActionHref } from "../utils/contactChannels";
import {
  buildPublicSharePayload,
  createAndCopyPublicShareLink,
  SHARE_LINK_COPIED_MESSAGE,
} from "../utils/publicShareLinks";
import { LEAD_STATUS_VALUES } from "../utils/leadStatusOptions";

const API_BASE = process.env.REACT_APP_API_BASE;
const EMPTY_VALUE = "\u2014";
const ADMIN_ROLES = new Set(["admin", "administrator", "system_user"]);

function formatTimeAgoShort(value) {
  return formatRelativeTimeShort(value, { emptyValue: EMPTY_VALUE });
}

function isAdminRole(value) {
  return ADMIN_ROLES.has(String(value || "").trim().toLowerCase());
}

function splitFullNameParts(value) {
  const normalized = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!normalized.length) {
    return { firstName: "", lastName: "" };
  }

  return {
    firstName: normalized[0] || "",
    lastName: normalized.slice(1).join(" "),
  };
}

const LEAD_BULK_STATUS_MENU_OPTIONS = LEAD_STATUS_VALUES.map((status) =>
  String(status || "").trim().toLowerCase() === "do not contact"
    ? { label: "Do Not Contact", value: "Do not contact" }
    : { label: status, value: status }
);

const LIST_TABS = {
  leads: "leads",
  salesPipeline: "sales-pipeline",
  contacts: "contacts",
};
const CONTACTS_TAB_LABEL = "Contacts";
const SALES_PIPELINE_STAGES = [
  "New Lead",
  "Contacted",
  "Discovery",
  "Proposal",
  "Client",
  "Closed",
];

const salesStageToLeadStatuses = (stage) => {
  const normalized = String(stage || "").trim().toLowerCase();
  if (normalized === "new lead") return ["New"];
  if (normalized === "closed") return ["Closed", "Lost", "Do not contact"];
  const matched = SALES_PIPELINE_STAGES.find((item) => item.toLowerCase() === normalized);
  return matched ? [matched] : [];
};

const salesStageToStatsFilter = (stage) => {
  const statuses = salesStageToLeadStatuses(stage);
  if (statuses.length !== 1) return "total";
  const normalizedStatus = String(statuses[0] || "").trim().toLowerCase();
  return ["new", "contacted", "discovery", "proposal", "client"].includes(normalizedStatus)
    ? `status:${normalizedStatus}`
    : "total";
};

const getSalesPipelineStage = (status) => {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "new" || normalized === "new lead") return "New Lead";
  if (["closed", "lost", "do not contact"].includes(normalized)) return "Closed";
  return String(status || "").trim() || "New Lead";
};
const LEAD_STATS_SLIDES = [
  { id: "total", label: "Leads" },
  { id: "status:new", label: "New" },
  { id: "contacts", label: "Contacts" },
  { id: "status:contacted", label: "Contacted" },
  { id: "sequences", label: "Sequences" },
  { id: "status:discovery", label: "Discovery" },
  { id: "status:proposal", label: "Proposal" },
  { id: "status:client", label: "Clients" },
  { id: "leadAgents", label: "Lead Agents" },
];
const SALES_STATS_SLIDES = LEAD_STATUS_VALUES.map((status) => ({
  id: `status:${status.toLowerCase()}`,
  label: status === "Do not contact" ? "Do Not Contact" : status,
}));
const LEAD_SEQUENCE_ENTITY_TYPES = new Set(["lead", "company", "client"]);
const TABLE_INFO_SOCIAL_TYPES = [
  "linkedin",
  "facebook",
  "twitter",
  "x",
  "google",
  "instagram",
  "youtube",
  "tiktok",
];
const hasRenderableActionHref = (type, href) =>
  Boolean(normalizeContactActionHref(type, href));
const normalizeSequenceEntityType = (value) => String(value ?? "").trim().toLowerCase();
const buildLeadSequenceEntityKeys = (entityType, entityId) => {
  const normalizedType =
    normalizeHiringManagerEntityType(entityType) || normalizeSequenceEntityType(entityType);
  const normalizedId = Number(entityId);
  if (!normalizedType || !Number.isFinite(normalizedId) || normalizedId <= 0) return [];
  return [`${normalizedType}:${normalizedId}`];
};
const hasLeadAgentActivity = (value) => /\bagent\b/i.test(String(value || "").trim());

const formatLeadAgentTaskRow = (row = {}, index = 0) =>
  `${index + 1}. ${
    cleanFilterValue(row?.lead_name || row?.job_title || row?.name) || "Unknown Lead"
  }${
    cleanFilterValue(row?.company || row?.name) ? ` | Company: ${cleanFilterValue(row.company || row.name)}` : ""
  }${
    cleanFilterValue(row?.website || row?.website_url)
      ? ` | Website: ${cleanFilterValue(row.website || row.website_url)}`
      : ""
  }${cleanFilterValue(row?.location) ? ` | Location: ${cleanFilterValue(row.location)}` : ""}${
    cleanFilterValue(row?.status) ? ` | Status: ${cleanFilterValue(row.status)}` : ""
  }${
    cleanFilterValue(row?.contact_name)
      ? ` | Contact: ${cleanFilterValue(row.contact_name)}`
      : ""
  }${
    cleanFilterValue(row?.source) ? ` | Source: ${cleanFilterValue(row.source)}` : ""
  }`;

function buildLeadAgentTaskPrompt({ taskValue = "", rows = [] } = {}) {
  const selectedRows = Array.isArray(rows) ? rows.filter(Boolean) : [];
  const selectionBlock = selectedRows.length
    ? selectedRows.map((row, index) => formatLeadAgentTaskRow(row, index)).join("\n")
    : "No leads selected.";

  switch (String(taskValue || "").trim()) {
    case "createProposal":
      return [
        "Generate a recruiting proposal draft for each selected lead.",
        "Include a short company summary, likely hiring pain points, suggested engagement model, scope, recommended next steps, and an outreach-ready summary.",
        "Save each draft in Content -> Proposals and provide a link to the saved proposal. Mark unknown commercial terms as to be agreed.",
        "",
        "Selected leads:",
        selectionBlock,
      ].join("\n");
    default:
      return [
        "Act as a sales outreach and RPO strategy assistant for the selected leads.",
        "For each lead, recommend the best next action, highlight any missing research, suggest outreach angles, and draft concise sales outreach guidance where useful.",
        "",
        "Selected leads:",
        selectionBlock,
      ].join("\n");
  }
}

const buildTableInfoActions = (actions = []) => {
  const normalizedActions = Array.isArray(actions) ? actions : [];
  const firstSocialAction = normalizedActions.find(
    (action) =>
      TABLE_INFO_SOCIAL_TYPES.includes(action?.type) &&
      hasRenderableActionHref(action?.type || "website", action?.href)
  );

  return [
    ...normalizedActions.filter((action) =>
      ["website", "email", "phone"].includes(action?.type)
    ),
    ...(firstSocialAction ? [firstSocialAction] : []),
  ];
};

const normalizeLeadRouteTab = (value) => {
  const normalized = String(value || "").toLowerCase();
  const validTabs = ["details", "stages", "matches", "activities"];
  return validTabs.includes(normalized) ? normalized : "details";
};

const normalizeCompanyOriginTab = (value) => {
  const normalized = String(value || "").toLowerCase();
  const validTabs = ["details", "leads", "contacts", "jobs", "candidates", "activities"];
  return validTabs.includes(normalized) ? normalized : "leads";
};

const getStatusChipSx = (status) => {
  const style = status === "Active"
    ? { backgroundColor: "#ECFDF3", color: "#16A34A" }
    : getSalesStatusChipSx(status);
  return {
    ...style,
    borderRadius: "999px",
    fontSize: 11,
    fontWeight: 500,
    height: 22,
    px: 1.5,
    lineHeight: 1.3,
  };
};

const HIRING_MANAGER_STATUS_CHIP_STYLES = {
  Contacted: { backgroundColor: "#F4EBFF", color: "#9C4FFF" },
  New: { backgroundColor: "#E0F2FE", color: "#0EA5E9" },
  Active: { backgroundColor: "#ECFDF3", color: "#12B76A" },
  "No stage": { backgroundColor: "#FFF1F3", color: "#F04438" },
  "Do not contact": { backgroundColor: "#F2F4F7", color: "#344054" },
  "Do not Contact": { backgroundColor: "#F2F4F7", color: "#344054" },
  default: { backgroundColor: "#F2F4F7", color: "#667085" },
};

const norm = (v) => String(v ?? "").replace(/\s+/g, " ").toLowerCase().trim();
const cleanFilterValue = (value) => String(value ?? "").trim();
const normalizeLeadFilterStatus = (value) => {
  const normalized = norm(value).replace(/\s+/g, " ");
  if (normalized === "new lead") return "new";
  if (normalized === "do not contact") return "do not contact";
  if (!normalized || normalized === "no status") return "no stage";
  return normalized;
};
const sameFilterValue = (left, right) => norm(left) === norm(right);
const removeFilterValueFromList = (values = [], target) =>
  (values || [])
    .filter((value) => !sameFilterValue(value, target))
    .map(cleanFilterValue)
    .filter(Boolean);
const pickFirstNonEmpty = (...values) => {
  for (const value of values) {
    const normalized = cleanFilterValue(value);
    if (normalized) return normalized;
  }
  return "";
};
const isGenericLeadLabel = (value) => /^lead\s*(?:#|no\.?|number)?\s*\d+$/i.test(cleanFilterValue(value));
const UNTITLED_JOB_TITLE = "Untitled job";
const getLeadJobTitle = (lead = {}) =>
  pickFirstNonEmpty(
    lead.job_title,
    lead.jobTitle,
    lead.position_title,
    lead.raw?.job_title,
    lead.raw?.jobTitle,
    lead.raw?.position_title
  ) || UNTITLED_JOB_TITLE;
const getLeadDisplayName = (lead = {}) => {
  const title = pickFirstNonEmpty(
    lead.job_title,
    lead.jobTitle,
    lead.lead_name,
    lead.title,
    lead.position_title
  );
  if (title && !isGenericLeadLabel(title)) return title;

  const contactName = pickFirstNonEmpty(lead.contact_name, lead.contactName);
  if (contactName) return contactName;

  const companyName = pickFirstNonEmpty(lead.company_name, lead.company, lead.name);
  return companyName || "Untitled lead";
};
const normalizeComparableEmail = (value) => cleanFilterValue(value).toLowerCase();
const normalizeComparablePhone = (value) => cleanFilterValue(value).replace(/\D/g, "");
const getAvatarLetters = (value, fallback = "C") => {
  const initials = cleanFilterValue(value)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.replace(/[^A-Za-z0-9]/g, "").charAt(0).toUpperCase())
    .filter(Boolean)
    .join("");

  return initials || fallback;
};

const META_TAG_BASE_SX = {
  borderRadius: "999px",
  fontSize: 11,
  fontWeight: 600,
  height: 24,
  px: 1.25,
  lineHeight: 1.2,
};

const LEAD_AGENT_TAG_SX = {
  backgroundColor: "#F4EBFF",
  color: "#7F56D9",
};

const getMetaChipSx = (label, styleMap = {}, fallbackStyle = {}) => {
  const normalizedLabel = cleanFilterValue(label);
  const directMatch = styleMap[normalizedLabel];
  const looseMatch = Object.entries(styleMap).find(([key]) => sameFilterValue(key, normalizedLabel));
  const resolvedStyle = directMatch || looseMatch?.[1] || styleMap.default || fallbackStyle;

  return {
    ...META_TAG_BASE_SX,
    ...(resolvedStyle || {}),
  };
};

const normalizeLeadTypeLabel = (value) => {
  const normalized = norm(value);
  if (!normalized) return "";
  if (normalized.includes("job")) return "Job Posting";
  if (normalized.includes("fund")) return "Funding";
  if (normalized.includes("referr")) return "Referral";
  if (normalized.includes("signal")) return "Hiring Signal";
  if (normalized.includes("word of mouth")) return "Word of Mouth";
  if (normalized.includes("manual")) return "Manual";
  if (normalized.includes("agent")) return "Agent Generated";
  return cleanFilterValue(value);
};

const resolveLeadAgentLabel = (lead = {}) => {
  const explicitLabel = pickFirstNonEmpty(
    lead.lead_agent,
    lead.lead_agent_name,
    lead.agent_name,
    lead.ai_agent,
    lead.ai_agent_name,
    lead.raw?.lead_agent,
    lead.raw?.lead_agent_name,
    lead.raw?.agent_name,
    lead.raw?.ai_agent,
    lead.raw?.ai_agent_name
  );

  if (explicitLabel) return explicitLabel;

  const activityLabel = pickFirstNonEmpty(
    lead.last_activity_title,
    lead.last_activity_type,
    lead.raw?.last_activity_title,
    lead.raw?.last_activity_type
  );
  if (hasLeadAgentActivity(activityLabel)) return activityLabel;

  const sourceLabel = pickFirstNonEmpty(lead.source, lead.raw?.source);
  if (/\bagent\b/i.test(sourceLabel)) return "Lead Agent";

  return "";
};

const inferLeadType = (lead = {}) => {
  const explicitType = normalizeLeadTypeLabel(
    pickFirstNonEmpty(lead.lead_type, lead.type, lead.raw?.lead_type, lead.raw?.type)
  );
  if (explicitType) return explicitType;

  const sourceValue = norm(lead.source || lead.raw?.source);
  if (sourceValue.includes("referral")) return "Referral";
  if (sourceValue.includes("word of mouth")) return "Word of Mouth";
  if (sourceValue.includes("signal")) return "Hiring Signal";
  if (sourceValue.includes("agent")) return "Agent Generated";

  const hasFundingSignals = [
    lead.funding_stage,
    lead.last_funding_round_amount,
    lead.total_raised_to_date,
    lead.raw?.funding_stage,
    lead.raw?.last_funding_round_amount,
    lead.raw?.total_raised_to_date,
  ].some((value) => Boolean(cleanFilterValue(value)));
  if (hasFundingSignals || sourceValue.includes("crunchbase")) return "Funding";

  const hasJobPostingSignals = [
    lead.job_title,
    lead.lead_link,
    lead.other_link,
    lead.career_website,
    lead.raw?.job_title,
    lead.raw?.lead_link,
    lead.raw?.other_link,
    lead.raw?.career_website,
  ].some((value) => Boolean(cleanFilterValue(value)));
  if (hasJobPostingSignals) return "Job Posting";

  if (cleanFilterValue(lead.lead_agent)) return "Agent Generated";

  return "Manual";
};

const parseTagList = (value) => {
  if (Array.isArray(value)) {
    return [...new Set(value.map((item) => cleanFilterValue(item)).filter(Boolean))];
  }

  const normalized = cleanFilterValue(value);
  if (!normalized) return [];

  if (
    (normalized.startsWith("[") && normalized.endsWith("]")) ||
    (normalized.startsWith("{") && normalized.endsWith("}"))
  ) {
    try {
      const parsed = JSON.parse(normalized);
      if (Array.isArray(parsed)) {
        return [...new Set(parsed.map((item) => cleanFilterValue(item)).filter(Boolean))];
      }
    } catch {
      // fall back to delimiter splitting
    }
  }

  return [
    ...new Set(
      normalized
        .split(/[\n,|;/]+/)
        .map((item) => cleanFilterValue(item))
        .filter(Boolean)
    ),
  ];
};

const buildTechStackMeta = (row = {}) => {
  const tokens = [
    ...new Set(
      [
        ...parseTagList(row.tech_stack_tokens),
        ...parseTagList(row.hard_skills),
        ...parseTagList(row.technology_stack),
        ...parseTagList(row.recruitment_tools),
        ...parseTagList(row.skills),
      ].filter(Boolean)
    ),
  ];

  return {
    tokens,
    primary: tokens[0] || EMPTY_VALUE,
    extraCount: Math.max(0, tokens.length - 1),
    sortValue: tokens.join(" "),
  };
};

const getHiringManagerStatusChipSx = (status) => {
  const normalized = cleanFilterValue(status) || "No stage";
  if (/^sequence/i.test(normalized)) {
    return {
      backgroundColor: "#EAF2FF",
      color: "#3451F4",
      borderRadius: "999px",
      fontSize: 10,
      fontWeight: 500,
      height: 20,
      px: 1,
      lineHeight: 1.2,
    };
  }

  const style =
    HIRING_MANAGER_STATUS_CHIP_STYLES[normalized] || HIRING_MANAGER_STATUS_CHIP_STYLES.default;

  return {
    ...style,
    borderRadius: "999px",
    fontSize: 10,
    fontWeight: 500,
    height: 20,
    px: 1,
    lineHeight: 1.2,
  };
};

const toCsvValue = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

export function buildLeadContactDisplay(lead = {}) {
  const ownerName = lead.owner_name || lead.owner || "";
  const leadContactMatchesOwner =
    ownerName && lead.contact_name
      ? areSamePersonNames(lead.contact_name, ownerName)
      : false;
  const fallbackLeadContactName = leadContactMatchesOwner ? "" : lead.contact_name || "";
  const fallbackLeadContactEmail = leadContactMatchesOwner ? "" : lead.contact_email || "";
  const fallbackLeadContactPhone = leadContactMatchesOwner ? "" : lead.contact_phone || "";

  const companyPrimaryName = cleanFilterValue(lead.company_primary_contact_name);
  const companyPrimaryEmail = cleanFilterValue(lead.company_primary_contact_email);
  const companyPrimaryPhone = cleanFilterValue(lead.company_primary_contact_phone);
  const jobPrimaryName = cleanFilterValue(lead.job_primary_contact_name);
  const jobPrimaryEmail = cleanFilterValue(lead.job_primary_contact_email);
  const jobPrimaryPhone = cleanFilterValue(lead.job_primary_contact_phone);

  const hasCompanyPrimaryContactData = Boolean(
    companyPrimaryName || companyPrimaryEmail || companyPrimaryPhone
  );
  const hasJobPrimaryContactData = Boolean(
    jobPrimaryName || jobPrimaryEmail || jobPrimaryPhone
  );

  let contactSourceKind = "none";
  if (fallbackLeadContactName || fallbackLeadContactEmail || fallbackLeadContactPhone) {
    contactSourceKind = "lead_contact";
  } else if (hasCompanyPrimaryContactData) {
    contactSourceKind = "company_primary";
  } else if (hasJobPrimaryContactData) {
    contactSourceKind = "job_primary";
  }

  const displayContactName =
    pickFirstNonEmpty(
      fallbackLeadContactName,
      fallbackLeadContactEmail,
      fallbackLeadContactPhone,
      hasCompanyPrimaryContactData ? companyPrimaryName : "",
      hasCompanyPrimaryContactData ? companyPrimaryEmail : "",
      hasCompanyPrimaryContactData ? companyPrimaryPhone : "",
      hasJobPrimaryContactData ? jobPrimaryName : "",
      hasJobPrimaryContactData ? jobPrimaryEmail : "",
      hasJobPrimaryContactData ? jobPrimaryPhone : ""
    ) || EMPTY_VALUE;

  const displayContactEmail =
    contactSourceKind === "company_primary"
      ? companyPrimaryEmail
      : contactSourceKind === "job_primary"
        ? jobPrimaryEmail
        : fallbackLeadContactEmail;
  const displayContactPhone =
    contactSourceKind === "company_primary"
      ? companyPrimaryPhone
      : contactSourceKind === "job_primary"
        ? jobPrimaryPhone
        : fallbackLeadContactPhone;
  const displayContactRole =
    displayContactName && displayContactName !== EMPTY_VALUE
      ? normalizeContactRoleLabel(
          contactSourceKind === "company_primary"
            ? lead.company_primary_contact_role
            : contactSourceKind === "job_primary"
              ? lead.job_primary_contact_role
              : lead.contact_role || lead.contact_title,
          { fallback: "Primary Contact" }
        )
      : "";

  return {
    contactName: displayContactName,
    contactEmail: displayContactEmail,
    contactPhone: displayContactPhone,
    contactRole: displayContactRole,
    contactSourceKind,
  };
}

function resolveCompanyPrimaryContactAvatar(row = {}, companyContacts = []) {
  if (row.contact_source_kind !== "company_primary" || !Array.isArray(companyContacts)) {
    return "";
  }

  const targetName = cleanFilterValue(row.company_primary_contact_name || row.contact_name);
  const targetEmail = normalizeComparableEmail(
    row.company_primary_contact_email || row.email_url
  );
  const targetPhone = normalizeComparablePhone(
    row.company_primary_contact_phone || row.phone_url
  );

  const match = companyContacts.find((contact) => {
    const contactName = cleanFilterValue(contact?.name);
    const contactEmail = normalizeComparableEmail(contact?.email);
    const contactPhone = normalizeComparablePhone(contact?.phone);

    return (
      (targetName && contactName && areSamePersonNames(targetName, contactName)) ||
      (targetEmail && contactEmail && targetEmail === contactEmail) ||
      (targetPhone && contactPhone && targetPhone === contactPhone)
    );
  });

  return cleanFilterValue(match?.profile_image);
}

function mapLeadRowToListRow(l) {
  const contactDisplay = buildLeadContactDisplay(l);
  const lastSyncValue = getEntityLastSyncValue(l);
  const lastActivityValue = getEntityLastActivityValue(l) || l.created_at || "";
  const lastActivityType = getEntityLastActivityType(l, {
    emptyValue: "Lead created",
  });
  const companyName =
    pickFirstNonEmpty(l.company_name, l.name, l.company, l.raw?.company_name) ||
    EMPTY_VALUE;
  const leadName = getLeadDisplayName({
    ...l,
    // Some imported leads carry their person in a linked hiring
    // manager record instead of the lead row itself. Use that exact
    // person name before falling back to the company name.
    contact_name: l.contact_name || contactDisplay.contactName,
  });
  const leadAgentLabel = resolveLeadAgentLabel({
    ...l,
    source: l.source,
    last_activity_type: lastActivityType,
  });
  const leadTypeLabel = inferLeadType({
    ...l,
    source: l.source,
    job_title: l.job_title,
    lead_agent: leadAgentLabel,
  });

  return {
    id: l.id,
    company_id: l.company_id != null ? Number(l.company_id) : null,
    name: companyName,
    company: companyName,
    company_name: companyName,
    company_logo: l.company_logo,
    lead_name: leadName,
    // Older records may predate the required-title rule. Keep their
    // table cell meaningful while the edit form prompts for a real title.
    job_title: getLeadJobTitle(l),
    lead_type: leadTypeLabel,
    lead_agent: leadAgentLabel,
    website: l.website || l.career_website || "",
    career_website: l.career_website || "",
    lead_link: l.lead_link || "",
    other_link: l.other_link || "",
    status: l.status || "No stage",
    job_openings: Number(l.job_openings || 0),
    location: [l.location_city, l.location_state, l.location_country]
      .filter(Boolean)
      .join(", "),
    location_city: l.location_city || "",
    location_state: l.location_state || "",
    location_country: l.location_country || "",
    contact_name: contactDisplay.contactName,
    contact_role: contactDisplay.contactRole,
    contact_title:
      pickFirstNonEmpty(l.contact_title, l.contact_role, contactDisplay.contactRole) ||
      "",
    contact_source_kind: contactDisplay.contactSourceKind,
    contact_avatar_url: l.company_primary_contact_avatar || "",
    lead_contact_name: l.contact_name || "",
    source: l.source || EMPTY_VALUE,
    last_sync: formatTimeAgoShort(lastSyncValue),
    last_sync_at: toTimestampValue(lastSyncValue),
    last_activity: formatTimeAgoShort(lastActivityValue),
    last_activity_at: toTimestampValue(lastActivityValue),
    last_activity_type: lastActivityType,
    owner: l.owner_name || l.owner || EMPTY_VALUE,
    linkedin_url: l.linkedin_url || "",
    facebook_url: l.facebook_url || "",
    twitter_url: l.twitter_url || l.x_url || "",
    contact_email: contactDisplay.contactEmail,
    email_url: contactDisplay.contactEmail,
    phone_url: contactDisplay.contactPhone,
    website_url: l.website || l.career_website || "",
    created_at: l.created_at || "",
    industry: l.industry || "",
    funding_stage: l.funding_stage || "",
    last_funding_round_amount: l.last_funding_round_amount || "",
    total_raised_to_date: l.total_raised_to_date || "",
    hard_skills: l.hard_skills || "",
    soft_skills: l.soft_skills || "",
    technology_stack: l.technology_stack || "",
    recruitment_tools: l.recruitment_tools || "",
    skills: l.skills || "",
    salary_range: l.salary_range || l.compensation || "",
    compensation: l.compensation || l.salary_range || "",
    job_primary_contact_name: l.job_primary_contact_name || "",
    job_primary_contact_email: l.job_primary_contact_email || "",
    job_primary_contact_phone: l.job_primary_contact_phone || "",
    job_primary_contact_role: l.job_primary_contact_role || "",
    company_primary_contact_name: l.company_primary_contact_name || "",
    company_primary_contact_email: l.company_primary_contact_email || "",
    company_primary_contact_phone: l.company_primary_contact_phone || "",
    company_primary_contact_role: l.company_primary_contact_role || "",
    raw: l,
  };
}

function applyCompanyFilters(rows, filters) {
  if (!filters) return rows;

  const ai = norm(filters.ai);
  const companyNames = (filters.companyNames || []).map(norm).filter(Boolean);
  const techKeywords = (filters.techKeywords || []).map(norm).filter(Boolean);
  const locations = normalizeLocationFilterValues(filters.locations || []).map(norm).filter(Boolean);
  const owners = (filters.owners || []).map(norm).filter(Boolean);
  const statuses = (filters.statuses || []).map(normalizeLeadFilterStatus).filter(Boolean);
  const activities = (filters.activities || []).map(norm).filter(Boolean);
  const sources = (filters.sources || []).map(norm).filter(Boolean);

  const hasAny =
    ai ||
    companyNames.length ||
    techKeywords.length ||
    locations.length ||
    owners.length ||
    statuses.length ||
    activities.length ||
    sources.length;

  if (!hasAny) return rows;

  return rows.filter((r) => {
    const company = norm(r.company || r.name);
    const leadName = norm(r.lead_name || r.job_title);
    const website = norm(r.website);
    const status = normalizeLeadFilterStatus(r.status);
    const location = norm(r.location);
    const contact = norm(r.contact_name);
    const contactTitle = norm(r.contact_title || r.contact_role);
    const leadType = norm(r.lead_type);
    const source = norm(r.source);
    const owner = norm(r.owner || r.owner_name || r.team_member || r.assigned_team_member);
    const leadAgent = norm(r.lead_agent);
    const lastActivity = norm(r.last_activity_type || r.last_activity);
    const industry = norm(r.industry || "");
    const hardSkills = norm(buildDetailFieldOptions([r]).skills.join(" "));
    const softSkills = norm(r.soft_skills || "");
    const salary = norm(r.salary_range || r.compensation || "");

    const blob = [
      company,
      leadName,
      website,
      status,
      location,
      contact,
      contactTitle,
      leadType,
      source,
      owner,
      leadAgent,
      lastActivity,
      industry,
      hardSkills,
      softSkills,
      salary,
    ].join(" ");

    if (ai && !blob.includes(ai)) return false;
    if (companyNames.length && !companyNames.some((x) => company.includes(x))) {
      return false;
    }
    if (owners.length && !owners.some((x) => owner.includes(x))) return false;
    if (statuses.length && !statuses.includes(status)) {
      return false;
    }
    if (
      activities.length &&
      !activities.some((x) => lastActivity.includes(x))
    ) {
      return false;
    }
    if (sources.length && !sources.some((x) => source.includes(x))) {
      return false;
    }
    if (locations.length && !matchesLocationFilters(r, filters.locations)) {
      return false;
    }
    if (techKeywords.length && !techKeywords.every((kw) => blob.includes(kw))) {
      return false;
    }

    return true;
  });
}

function applyAdvancedCompanyFilters(rows, adv) {
  if (!adv) return rows;

  const ai = norm(adv.aiAgent);
  const companyName = norm(adv.companyName);
  const skills = (adv.skills || []).map(norm).filter(Boolean);
  const locations = normalizeLocationFilterValues(adv.locations || []).map(norm).filter(Boolean);
  const owner = (adv.owner || []).map(norm).filter(Boolean);
  const status = (adv.status || []).map(normalizeLeadFilterStatus).filter(Boolean);
  const activity = (adv.activity || []).map(norm).filter(Boolean);
  const source = (adv.source || []).map(norm).filter(Boolean);

  const cd = adv.company_details || {};
  const cdIndustry = (cd.industry || []).map(norm).filter(Boolean);

  const hasAny =
    ai ||
    companyName ||
    skills.length ||
    locations.length ||
    owner.length ||
    status.length ||
    activity.length ||
    source.length ||
    cdIndustry.length;

  if (!hasAny) return rows;

  return rows.filter((r) => {
    const company = norm(r.company || r.name);
    const leadName = norm(r.lead_name || r.job_title);
    const website = norm(r.website);
    const rStatus = normalizeLeadFilterStatus(r.status);
    const rLocation = norm(r.location);
    const rContact = norm(r.contact_name);
    const rContactTitle = norm(r.contact_title || r.contact_role);
    const rLeadType = norm(r.lead_type);
    const rSource = norm(r.source);
    const rOwner = norm(r.owner);
    const rLeadAgent = norm(r.lead_agent);
    const rLastActivity = norm(r.last_activity_type || r.last_activity);
    const rIndustry = norm(r.industry || "");
    const rHardSkills = norm(r.hard_skills || r.technology_stack || r.skills || r.recruitment_tools);
    const rSoftSkills = norm(r.soft_skills || "");
    const rSalary = norm(r.salary_range || r.compensation || "");

    const blob = [
      company,
      leadName,
      website,
      rStatus,
      rLocation,
      rContact,
      rContactTitle,
      rLeadType,
      rSource,
      rOwner,
      rLeadAgent,
      rLastActivity,
      rIndustry,
      rHardSkills,
      rSoftSkills,
      rSalary,
    ].join(" ");

    if (ai && !blob.includes(ai)) return false;
    if (companyName && !company.includes(companyName)) return false;
    if (skills.length && !skills.every((kw) => blob.includes(kw))) {
      return false;
    }
    if (locations.length && !matchesLocationFilters(r, adv.locations)) {
      return false;
    }
    if (owner.length && !owner.some((x) => rOwner.includes(x))) return false;
    if (status.length && !status.includes(rStatus)) {
      return false;
    }
    if (
      activity.length &&
      !activity.some((x) => rLastActivity.includes(x))
    ) {
      return false;
    }
    if (source.length && !source.some((x) => rSource.includes(x))) {
      return false;
    }
    if (
      cdIndustry.length &&
      !cdIndustry.some((x) => rIndustry.includes(x) || blob.includes(x))
    ) {
      return false;
    }

    return true;
  });
}

const headCellsLeads = [
  { id: "status", label: "Status", numeric: false, minWidth: 122, width: 122 },
  { id: "lead_name", label: "Job Title", numeric: false, minWidth: 205, width: 205 },
  { id: "contact_name", label: "Contact", numeric: false, minWidth: 205, width: 205 },
  { id: "company", label: "Company", numeric: false, minWidth: 180, width: 180 },
  { id: "lead_type", label: "Type", numeric: false, minWidth: 112, width: 112 },
  { id: "source", label: "Source", numeric: false, minWidth: 93, width: 93 },
  {
    id: "location",
    label: "Location",
    numeric: false,
    minWidth: 121, width: 121,
  },
  {
    id: "contact_information",
    label: "Info",
    numeric: false,
    sortable: false,
    minWidth: 120, width: 120,
  },
  { id: "last_activity_at", label: "Last Activity", numeric: false, minWidth: 127, width: 127 },
  { id: "owner", label: "Owner", numeric: false, minWidth: 126, width: 126 },
  { id: "lead_agent", label: "Lead Agent", numeric: false, minWidth: 110, width: 110 },
  { id: "actions", label: "Actions", numeric: false, sortable: false, minWidth: 52, width: 52 },
];
const headCellsSalesPipeline = headCellsLeads.map((cell) =>
  cell.id === "status" ? { ...cell, label: "Current Stage" } : cell
);

const BulkActionsLeadsBar = ({
  selectedCount,
  onAddToSequence,
  onContactLead,
  onLaunchAgent,
  onCreateProposal,
  onExportCsv,
  onArchive,
  onDelete,
  onChangeStatus,
  statusOptions = [],
  statusSaving = false,
  statusFeedback = null,
  onStatusFeedbackClose,
  disabledDelete,
  assignOwnerActionNode,
  showDelete = true,
  exportContext,
}) => (
  <SharedBulkActionsBar
    selectedCount={selectedCount}
    selectionLabel="lead"
    exportContext={exportContext}
    actions={[
      {
        type: "menu",
        label: statusSaving ? "Saving..." : "Change Status",
        options: statusOptions,
        onSelect: onChangeStatus,
        feedback: statusFeedback,
        onFeedbackClose: onStatusFeedbackClose,
        disabledMessage:
          !selectedCount && !statusSaving ? "Please select an item to change status." : "",
        disabled: !selectedCount || statusSaving,
      },
      { type: "divider" },
      assignOwnerActionNode || {
        type: "button",
        label: "Assign Owner",
        disabled: true,
      },
      { type: "divider" },
      {
        type: "button",
        label: "Add to Sequence",
        startIcon: null,
        onClick: onAddToSequence,
        confirm: false,
        disabled: !selectedCount,
      },
      { type: "divider", sx: { width: "1.5px" } },
      {
        type: "button",
        label: "Launch Agent",
        onClick: onLaunchAgent,
        confirm: false,
      },
      { type: "divider" },
      {
        type: "button",
        label: "Create Proposal",
        startIcon: <ProposalActionIcon size={18} />,
        onClick: onCreateProposal,
        confirm: false,
        disabled: !selectedCount,
      },
      { type: "divider" },
      {
        type: "button",
        label: "CSV Export",
        onClick: onExportCsv,
        confirm: false,
      },
      { type: "divider" },
      {
        type: "button",
        label: "Archive",
        onClick: onArchive,
        confirmLabel: "Archive",
        confirmMessage: ({ selectedCount: nextSelectedCount = 0 }) =>
          `Soft archive ${nextSelectedCount || 0} selected ${
            nextSelectedCount === 1 ? "lead" : "leads"
          } by moving them to Do Not Contact?`,
        disabled: !selectedCount || statusSaving,
      },
      showDelete ? { type: "divider" } : null,
      showDelete
        ? {
            type: "button",
            label: "Delete",
            onClick: onDelete,
            confirmLabel: "Delete",
            confirmMessage: ({ selectedCount: nextSelectedCount = 0 }) =>
              `Delete ${nextSelectedCount || 0} selected ${
                nextSelectedCount === 1 ? "lead" : "leads"
              }? You will need to confirm one more time before anything is removed.`,
            disabled: disabledDelete,
          }
        : null,
    ].filter(Boolean)}
    rightButton={{
      label: "Contact Lead",
      className: "leads-contact-action",
      startIcon: <Box component="span" aria-hidden="true" sx={{ width: 20, height: 20, backgroundColor: "currentColor", mask: `url(${leadContactIcon}) center / contain no-repeat`, WebkitMask: `url(${leadContactIcon}) center / contain no-repeat` }} />,
      background: "var(--primary-color)",
      onClick: onContactLead,
    }}
    sx={{ mb: 2 }}
  />
);

const Leads = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuthController();

  const [rows, setRows] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [leadTableTotal, setLeadTableTotal] = useState(0);
  const [leadStats, setLeadStats] = useState(null);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsRowsPerPage, setLeadsRowsPerPage] = useState(10);
  const [contactRows, setContactRows] = useState([]);
  const [contactRowsTotal, setContactRowsTotal] = useState(null);
  const [contactTablePage, setContactTablePage] = useState(1);
  const [contactFilters, setContactFilters] = useState({});
  const [contactTableRowsPerPage, setContactTableRowsPerPage] = useState(10);
  const [contactTableTotal, setContactTableTotal] = useState(0);
  const [sequenceRows, setSequenceRows] = useState([]);
  const [listTab, setListTab] = useState(LIST_TABS.leads);
  const [activeStatId, setActiveStatId] = useState("total");
  const overviewMetric = useMemo(
    () => new URLSearchParams(location.search).get("overviewMetric") || "",
    [location.search]
  );
  const salesPipelineStageFromUrl = useMemo(
    () => new URLSearchParams(location.search).get("stage") || "",
    [location.search]
  );

  useEffect(() => {
    const view = new URLSearchParams(location.search).get("view");
    if (view !== LIST_TABS.salesPipeline) return;

    setListTab(LIST_TABS.salesPipeline);
    const statuses = salesStageToLeadStatuses(salesPipelineStageFromUrl);
    const pipelineFilters = { ...DEFAULT_FILTERS, statuses };
    setFiltersDraft(pipelineFilters);
    setFiltersApplied(pipelineFilters);
  }, [location.search, salesPipelineStageFromUrl]);

  useEffect(() => {
    const statusByMetric = {
      new_leads: "New",
      lead_discovery: "Discovery",
      lead_proposal: "Proposal",
    };
    const status = statusByMetric[overviewMetric];
    if (status) {
      setListTab(LIST_TABS.leads);
      const metricFilters = { ...DEFAULT_FILTERS, statuses: [status] };
      setFiltersDraft(metricFilters);
      setFiltersApplied(metricFilters);
    }
  }, [overviewMetric]);
  const [search, setSearch] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(DEFAULT_FILTERS);
  const [filtersDraft, setFiltersDraft] = useState(DEFAULT_FILTERS);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advancedApplied, setAdvancedApplied] = useState(null);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [isAddToSequenceOpen, setIsAddToSequenceOpen] = useState(false);
  const [isAddLeadOpen, setIsAddLeadOpen] = useState(false);
  const [isCsvImportOpen, setIsCsvImportOpen] = useCsvImport();
  const [leadForm, setLeadForm] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusToast, setStatusToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [actionToast, setActionToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [isAgentDialogOpen, setIsAgentDialogOpen] = useState(false);
  const [agentDialogInitialPrompt, setAgentDialogInitialPrompt] = useState("");
  const [agentDialogSubmitOnOpen, setAgentDialogSubmitOnOpen] = useState(false);
  const [pendingAgentContentTab, setPendingAgentContentTab] = useState("");
  const [leadDialogAction, setLeadDialogAction] = useState("create");
  const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState(null);
  const [rowMenuLead, setRowMenuLead] = useState(null);
  const [contactMenuAnchorEl, setContactMenuAnchorEl] = useState(null);
  const [rowMenuContact, setRowMenuContact] = useState(null);
  const [selectedLeadPreview, setSelectedLeadPreview] = useState(null);
  const [selectedHiringManager, setSelectedHiringManager] = useState(null);
  const [selectedLeadCompany, setSelectedLeadCompany] = useState(null);
  const closingLeadDetailRef = useRef(false);
  const attemptedLeadLocationBackfillKeysRef = useRef(new Set());
  const isAdminUser = isAdminRole(user?.role);
  const sidebarResetToken = getSidebarResetToken(location, "/leads");
  const detailRouteSearch = sidebarResetToken ? "" : location.search;
  const detailLeadId = useMemo(() => {
    const nextLeadId = Number(new URLSearchParams(detailRouteSearch).get("leadId") || 0);
    return Number.isFinite(nextLeadId) && nextLeadId !== 0 ? nextLeadId : null;
  }, [detailRouteSearch]);
  const detailSyntheticLead = useMemo(() => {
    const candidate = location.state?.syntheticLead;
    if (!candidate || typeof candidate !== "object" || !detailLeadId || detailLeadId > 0) {
      return null;
    }

    const candidateId = Number(
      candidate?.lead_id ?? candidate?.leadId ?? candidate?.id ?? 0
    );
    return Number.isFinite(candidateId) && candidateId === detailLeadId
      ? candidate
      : null;
  }, [detailLeadId, location.state]);
  const detailTab = useMemo(
    () => normalizeLeadRouteTab(new URLSearchParams(detailRouteSearch).get("tab")),
    [detailRouteSearch]
  );
  const detailLead = useMemo(() => {
    if (!detailLeadId) return null;
    if (detailLeadId < 0) {
      return detailSyntheticLead || { id: detailLeadId };
    }
    return (
      rows.find((row) => Number(row?.id) === detailLeadId) || {
        id: detailLeadId,
      }
    );
  }, [detailLeadId, detailSyntheticLead, rows]);
  const handleDetailLeadChange = useCallback((nextLead = {}) => {
    const nextId = Number(nextLead?.lead_id ?? nextLead?.leadId ?? nextLead?.id ?? 0);
    if (!Number.isFinite(nextId) || nextId <= 0) return;

    setRows((previous) =>
      previous.map((row) => {
        const rowId = Number(row?.lead_id ?? row?.leadId ?? row?.id ?? 0);
        if (rowId !== nextId) return row;

        return {
          ...row,
          ...nextLead,
          id: row.id ?? nextId,
          status: nextLead.status ?? row.status,
          raw: row?.raw
            ? { ...row.raw, ...nextLead, status: nextLead.status ?? row.raw.status }
            : row.raw,
        };
      })
    );
  }, []);
  const [detailLeadResolvedTitle, setDetailLeadResolvedTitle] = useState("");
  const detailLeadLabel = useMemo(() => {
    if (detailLeadResolvedTitle) return detailLeadResolvedTitle;
    if (!detailLeadId) return "Lead";
    return getLeadDisplayName(detailLead);
  }, [detailLead, detailLeadId, detailLeadResolvedTitle]);
  const detailOriginContext = useMemo(() => {
    const origin = location.state?.companyOrigin;
    if (!origin?.parentCompany?.id) return null;

    return {
      parentCompany: origin.parentCompany,
      parentTab: normalizeCompanyOriginTab(origin.parentTab),
      returnListTab: origin.returnListTab === "clients" ? "clients" : "companies",
    };
  }, [location.state]);
  const detailOriginCompanyLabel = useMemo(() => {
    if (!detailOriginContext?.parentCompany) return "";
    return (
      detailOriginContext.parentCompany?.name ||
      detailOriginContext.parentCompany?.company_name ||
      "Company"
    );
  }, [detailOriginContext]);
  const detailOriginParentTabLabel = useMemo(() => {
    if (!detailOriginContext) return "";
    return findTabLabel(COMPANY_PROFILE_TABS, detailOriginContext.parentTab);
  }, [detailOriginContext]);
  const [, setActiveDetailTab] = useState(detailTab);

  useEffect(() => {
    setDetailLeadResolvedTitle("");
  }, [detailLeadId]);

  const selectedCount = selectedLeadIds.length;

  useEffect(() => {
    if (!sidebarResetToken) return;

    setShowAdvanced(false);
    setIsFilterOpen(false);
    setActiveStatId("total");
    setListTab(LIST_TABS.leads);
    setSelectedLeadIds([]);
    setSelectedContactIds([]);
    setRowMenuAnchorEl(null);
    setRowMenuLead(null);
    setLeadDialogAction("create");
    setSelectedLeadPreview(null);
    setSelectedHiringManager(null);
    setSelectedLeadCompany(null);

    if (location.search) {
      navigate(location.pathname, { replace: true });
    }
  }, [location.pathname, location.search, navigate, sidebarResetToken]);

  useLeadRouteStat({
    listTab,
    overviewMetric,
    pipelineStatId: salesStageToStatsFilter(salesPipelineStageFromUrl),
    setActiveStatId,
  });

  useEffect(() => {
    setSelectedLeadIds([]);
    setSelectedContactIds([]);
    setRowMenuAnchorEl(null);
    setRowMenuLead(null);
    setSelectedLeadPreview(null);
    setSelectedHiringManager(null);
    setSelectedLeadCompany(null);
  }, [listTab, overviewMetric]);

  useEffect(() => {
    setActiveDetailTab(detailTab);
  }, [detailTab]);

  const getToken = useCallback(() => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      localStorage.getItem("jwt") ||
      sessionStorage.getItem("token") ||
      sessionStorage.getItem("accessToken") ||
      sessionStorage.getItem("jwt") ||
      ""
    );
  }, []);

  const authHeaders = useCallback(() => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);

  const loadSequenceRows = useCallback(async () => {
    const response = await fetch(`${API_BASE}/api/sequences`, {
      headers: { ...authHeaders() },
      credentials: "include",
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(text || `Failed to fetch sequences (${response.status})`);
    }

    const data = await response.json().catch(() => ({}));
    return Array.isArray(data) ? data : data.rows || [];
  }, [authHeaders]);

  const backfillLeadLocations = useCallback(
    async (leadRows = []) => {
      const leadIds = Array.from(
        new Set(
          (leadRows || [])
            .filter((row) =>
              Boolean(
                cleanFilterValue(
                  row?.location ||
                    row?.location_city ||
                    row?.location_state ||
                    row?.location_country ||
                    row?.locations_json ||
                    row?.locationsJson
                )
              )
            )
            .map((row) => Number(row?.id))
            .filter((value) => Number.isFinite(value) && value > 0)
        )
      ).sort((a, b) => a - b);

      if (!leadIds.length) return false;

      const requestKey = leadIds.join(",");
      if (!requestKey || attemptedLeadLocationBackfillKeysRef.current.has(requestKey)) {
        return false;
      }
      attemptedLeadLocationBackfillKeysRef.current.add(requestKey);

      try {
        const response = await fetch(`${API_BASE}/api/leads/location-state-backfill`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
          },
          body: JSON.stringify({ ids: leadIds }),
        });

        if (!response.ok) return false;

        const data = await response.json().catch(() => ({}));
        return Number(data?.updated_count || 0) > 0;
      } catch (error) {
        console.error("Failed to backfill lead locations:", error);
        return false;
      }
    },
    [authHeaders]
  );

  const openLeadDetails = useCallback(
    (row, tab = "details", options = {}) => {
      const leadData = row?.raw || row || {};
      const leadId = Number(
        leadData?.lead_id ?? leadData?.leadId ?? leadData?.id ?? row ?? 0
      );
      const linkedJobId = Number(
        leadData?.job_id ??
          leadData?.jobId ??
          leadData?.related_job_id ??
          leadData?.relatedJobId ??
          0
      );
      const isSyntheticLeadRoute =
        Number.isFinite(leadId) && leadId < 0 && Number.isFinite(linkedJobId) && linkedJobId > 0;
      if (!Number.isFinite(leadId) || leadId === 0 || (leadId < 0 && !isSyntheticLeadRoute)) return;

      closingLeadDetailRef.current = false;

      const params = new URLSearchParams();
      params.set("leadId", String(leadId));
      params.set("tab", normalizeLeadRouteTab(tab));

      setShowAdvanced(false);
      setIsFilterOpen(false);
      setListTab(LIST_TABS.leads);
      setSelectedLeadPreview(null);
      setSelectedHiringManager(null);
      setSelectedLeadCompany(null);
      const nextState = {};
      if (options?.parentCompany?.id) {
        nextState.companyOrigin = {
          parentCompany: options.parentCompany,
          parentTab: normalizeCompanyOriginTab(options?.parentTab),
          returnListTab: options?.returnListTab === "clients" ? "clients" : "companies",
        };
      }
      if (isSyntheticLeadRoute) {
        nextState.syntheticLead = leadData;
      }
      navigate(
        {
          pathname: location.pathname,
          search: `?${params.toString()}`,
        },
        {
          replace: false,
          state: Object.keys(nextState).length ? nextState : null,
        }
      );
    },
    [location.pathname, navigate]
  );

  const closeLeadDetails = useCallback(() => {
    closingLeadDetailRef.current = true;
    setShowAdvanced(false);
    setIsFilterOpen(false);
    setIsAddLeadOpen(false);
    setLeadForm(null);
    setLeadDialogAction("create");
    setIsAddToSequenceOpen(false);
    setSelectedLeadIds([]);
    setSelectedContactIds([]);
    setSelectedLeadPreview(null);
    setSelectedHiringManager(null);
    setSelectedLeadCompany(null);
    setListTab(LIST_TABS.leads);
    setActiveStatId("total");

    navigate(
      {
        pathname: location.pathname,
        search: "",
      },
      {
        replace: true,
        state: createSidebarResetState(location.pathname),
      }
    );
  }, [location.pathname, navigate]);

  const openOriginCompany = useCallback(() => {
    if (!detailOriginContext?.parentCompany?.id) {
      closeLeadDetails();
      return;
    }

    const { parentCompany, parentTab, returnListTab } = detailOriginContext;
    const payload = buildOpenCompanyNavPayload({
      company: parentCompany,
      defaultTab: parentTab,
      returnListTab,
      entityType: "company",
    });
    if (!payload) {
      closeLeadDetails();
      return;
    }

    navigate(
      {
        pathname: "/companies",
        search: `?companyId=${encodeURIComponent(parentCompany.id)}&tab=${encodeURIComponent(
          parentTab
        )}&entityType=company&returnTab=${encodeURIComponent(returnListTab)}`,
      },
      {
        replace: false,
        state: payload,
      }
    );
  }, [closeLeadDetails, detailOriginContext, navigate]);

  const openLeadPreview = useCallback((row) => {
    if (!row) return;
    setSelectedHiringManager(null);
    setSelectedLeadCompany(null);
    setSelectedLeadPreview(row);
  }, []);

  const closeLeadPreview = useCallback(() => {
    setSelectedLeadPreview(null);
  }, []);

  const handleShowMoreLeadPreview = useCallback(
    (row) => {
      const nextLead = row || selectedLeadPreview;
      closeLeadPreview();
      if (!nextLead) return;
      openLeadDetails(nextLead, "details");
    },
    [closeLeadPreview, openLeadDetails, selectedLeadPreview]
  );

  const openHiringManagerDrawer = useCallback((row) => {
    if (!row) return;
    setSelectedLeadPreview(null);
    setSelectedHiringManager(row);
  }, []);

  const openLeadCompanyDrawer = useCallback((row) => {
    if (!row) return;
    setSelectedLeadPreview(null);
    setSelectedLeadCompany(row);
  }, []);

  const closeLeadCompanyDrawer = useCallback(() => {
    setSelectedLeadCompany(null);
  }, []);

  const handleShowMoreLeadCompany = useCallback(
    (tab, row) => {
      const normalizedEntityType = String(row?.entityType || row?.entity_type || "lead").toLowerCase();
      const requestedCompanyTab = normalizeCompanyOriginTab(tab);

      closeLeadCompanyDrawer();

      if (normalizedEntityType === "company" || normalizedEntityType === "client") {
        const companyId = Number(row?.company_id || row?.companyId || row?.id || 0);
        const payload = buildOpenCompanyNavPayload({
          company: row,
          defaultTab: requestedCompanyTab,
          returnListTab: "companies",
          entityType: "company",
        });
        if (!Number.isFinite(companyId) || companyId <= 0 || !payload) return;

        navigate(
          {
            pathname: "/companies",
            search: `?companyId=${encodeURIComponent(companyId)}&tab=${encodeURIComponent(
              requestedCompanyTab
            )}&entityType=company&returnTab=companies`,
          },
          {
            replace: false,
            state: payload,
          }
        );
        return;
      }

      const nextTab = requestedCompanyTab === "activities" ? "activities" : "details";
      openLeadDetails(row, nextTab);
    },
    [closeLeadCompanyDrawer, navigate, openLeadDetails]
  );

  const closeHiringManagerDrawer = useCallback(() => {
    setSelectedHiringManager(null);
  }, []);

  const handleShowMoreHiringManager = useCallback(
    (row) => {
      const entityType = normalizeHiringManagerEntityType(row?.entity_type);
      const entityId = Number(row?.entity_id || 0);

      closeHiringManagerDrawer();

      if (!Number.isFinite(entityId) || entityId <= 0) return;

      if (entityType === "lead") {
        openLeadDetails({ id: entityId }, "details");
        return;
      }

      if (entityType === "company" || entityType === "client") {
        const companyName = row?.company || row?.entity_name || "Company";
        const payload = buildOpenCompanyNavPayload({
          company: {
            id: entityId,
            company_id: entityId,
            companyId: entityId,
            name: companyName,
            company_name: companyName,
            logo: row?.entity_logo || "",
            company_logo: row?.entity_logo || "",
          },
          defaultTab: "contacts",
          returnListTab: "companies",
          entityType: "company",
        });
        if (!payload) return;

        navigate(
          {
            pathname: "/companies",
            search: `?companyId=${encodeURIComponent(entityId)}&tab=contacts&entityType=company&returnTab=companies`,
          },
          {
            replace: false,
            state: payload,
          }
        );
      }
    },
    [closeHiringManagerDrawer, navigate, openLeadDetails]
  );

  const showActionToast = useCallback((message, severity = "success") => {
    setActionToast({
      open: true,
      severity,
      message: String(message || "").trim() || "Action completed.",
    });
  }, []);

  const handleImportLeads = useCallback(async (importRows, setProgress) => {
    setProgress(15);
    const response = await fetch(`${API_BASE}/api/leads/import`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      credentials: "include",
      body: JSON.stringify({ rows: importRows }),
    });
    setProgress(85);
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result?.error || "Failed to import leads.");
    setReloadKey((value) => value + 1);
    showActionToast(`${result.created || 0} lead${result.created === 1 ? "" : "s"} added, ${result.updated || 0} updated${result.skipped ? `, ${result.skipped} skipped` : ""}.`);
    setProgress(100);
    return result;
  }, [authHeaders, showActionToast]);

  const closeActionToast = useCallback((_event, reason) => {
    if (reason === "clickaway") return;
    setActionToast((prev) => ({ ...prev, open: false }));
  }, []);

  const leadDialogInitialScreen = leadDialogAction === "create" ? "start" : "overview";
  const leadDialogTitleOverride =
    leadDialogAction === "duplicate" ? "Duplicate Lead" : undefined;
  const leadDialogSubmitLabel =
    leadDialogAction === "duplicate" ? "Create Duplicate" : undefined;

  const buildLeadDialogValue = useCallback((detail = {}, row = {}, { duplicate = false } = {}) => {
    const normalizeDialogText = (value) => {
      const normalized = cleanFilterValue(value);
      return normalized === EMPTY_VALUE ? "" : normalized;
    };

    const contactName = pickFirstNonEmpty(
      detail.contact_name,
      detail.primary_contact,
      row?.raw?.contact_name,
      row?.contact_name
    );
    const { firstName, lastName } = splitFullNameParts(contactName);
    const rawLeadId = Number(detail.id ?? row?.id ?? row?.lead_id ?? row?.leadId ?? 0);
    const rawCompanyId = Number(
      detail.companyId ?? detail.company_id ?? row?.company_id ?? row?.raw?.company_id ?? 0
    );

    return {
      id: duplicate || !Number.isFinite(rawLeadId) || rawLeadId <= 0 ? null : rawLeadId,
      lead_id: duplicate || !Number.isFinite(rawLeadId) || rawLeadId <= 0 ? null : rawLeadId,
      companyName: normalizeDialogText(
        pickFirstNonEmpty(detail.companyName, detail.company_name, row?.name, row?.raw?.company_name)
      ),
      companyId: Number.isFinite(rawCompanyId) && rawCompanyId > 0 ? rawCompanyId : null,
      companyLogo: normalizeDialogText(
        pickFirstNonEmpty(detail.companyLogo, detail.company_logo, row?.company_logo, row?.raw?.company_logo)
      ),
      website: normalizeDialogText(
        pickFirstNonEmpty(
          detail.company_website,
          detail.companyWebsite,
          row?.website_url,
          row?.website,
          row?.raw?.website
        )
      ),
      source:
        normalizeDialogText(pickFirstNonEmpty(detail.source, row?.source, row?.raw?.source)) ||
        "LinkedIn",
      firstName,
      lastName,
      email: normalizeDialogText(
        pickFirstNonEmpty(detail.contact_email, detail.company_email, row?.email_url)
      ),
      phoneNumber: normalizeDialogText(
        pickFirstNonEmpty(detail.contact_phone, detail.company_phone, row?.phone_url)
      ),
      jobTitle: normalizeDialogText(pickFirstNonEmpty(detail.job_title, row?.raw?.job_title)),
      jobOpenings:
        detail.job_openings !== undefined && detail.job_openings !== null
          ? String(detail.job_openings)
          : row?.job_openings !== undefined && row?.job_openings !== null
            ? String(row.job_openings)
            : "",
      location: normalizeDialogText(pickFirstNonEmpty(detail.location, row?.location)),
      leadLink: normalizeDialogText(
        pickFirstNonEmpty(detail.lead_link, detail.career_site_url, row?.raw?.lead_link)
      ),
      linkSource:
        normalizeDialogText(pickFirstNonEmpty(detail.source, row?.source, row?.raw?.source)) ||
        "LinkedIn",
      linkedin: normalizeDialogText(pickFirstNonEmpty(detail.linkedin_url, row?.linkedin_url)),
      otherLink: normalizeDialogText(pickFirstNonEmpty(detail.other_link, row?.raw?.other_link)),
      notes: normalizeDialogText(
        pickFirstNonEmpty(
          detail.full_company_description,
          detail.elevator_pitch,
          row?.raw?.full_company_description
        )
      ),
      teamMember: normalizeDialogText(
        pickFirstNonEmpty(detail.owner, row?.owner, row?.raw?.owner_name)
      ),
      leadStatus:
        normalizeDialogText(pickFirstNonEmpty(detail.status, row?.status, row?.raw?.status)) ||
        "New",
    };
  }, []);

  const loadLeadDialogValue = useCallback(
    async (row, { duplicate = false } = {}) => {
      const leadId = Number(row?.id ?? row?.lead_id ?? row?.leadId ?? 0);
      let detail = {};

      if (Number.isFinite(leadId) && leadId > 0) {
        try {
          const response = await fetch(`${API_BASE}/api/leads/${leadId}/info`, {
            headers: { ...authHeaders() },
            credentials: "include",
          });

          if (!response.ok) {
            const text = await response.text().catch(() => "");
            throw new Error(text || `Failed to load lead details (${response.status})`);
          }

          detail = await response.json().catch(() => ({}));
        } catch (error) {
          console.error("Failed to load lead details:", error);
          showActionToast(
            "Could not load the full lead record. Opening with current table data.",
            "warning"
          );
        }
      }

      return buildLeadDialogValue(detail, row, { duplicate });
    },
    [authHeaders, buildLeadDialogValue, showActionToast]
  );

  const openContactMenu = useCallback((event, row) => {
    event.stopPropagation?.();
    setContactMenuAnchorEl(event.currentTarget);
    setRowMenuContact(row);
  }, []);

  const closeContactMenu = useCallback(() => {
    setContactMenuAnchorEl(null);
    setRowMenuContact(null);
  }, []);

  const openContactProfile = useCallback(() => {
    const contact = rowMenuContact;
    closeContactMenu();
    const contactId = Number(contact?.id || 0);
    if (!Number.isFinite(contactId) || contactId <= 0) return;
    navigate(`/contacts/${encodeURIComponent(String(contactId))}`, {
      state: { contact, companyName: contact?.company || contact?.entity_name },
    });
  }, [closeContactMenu, navigate, rowMenuContact]);

  const shareContact = useCallback(async () => {
    const contact = rowMenuContact;
    closeContactMenu();
    if (!contact) return;
    try {
      await createAndCopyPublicShareLink({
        apiBase: API_BASE,
        payload: buildPublicSharePayload("contact", contact),
      });
      showActionToast(SHARE_LINK_COPIED_MESSAGE);
    } catch (error) {
      showActionToast(error?.message || "Failed to create share link.", "error");
    }
  }, [closeContactMenu, rowMenuContact, showActionToast]);

  const editContact = useCallback(() => {
    // Contact profiles already expose the saved edit form. Keep editing in
    // that dedicated workflow while the list remains a lightweight index.
    openContactProfile();
  }, [openContactProfile]);

  const addContactNote = useCallback(async () => {
    const contact = rowMenuContact;
    closeContactMenu();
    const contactId = Number(contact?.id || 0);
    if (!Number.isFinite(contactId) || contactId <= 0) return;
    const details = window.prompt(`Add a note for ${contact?.name || "this contact"}:`);
    if (!String(details || "").trim()) return;

    try {
      const response = await fetch(`${API_BASE}/api/activities`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        credentials: "include",
        body: JSON.stringify({
          entityType: "contact",
          entityId: contactId,
          activityType: "note",
          title: "Note",
          summary: String(details).trim().slice(0, 255),
          details: String(details).trim(),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || "Failed to create note.");
      showActionToast("Note added.");
    } catch (error) {
      showActionToast(error?.message || "Failed to create note.", "error");
    }
  }, [authHeaders, closeContactMenu, rowMenuContact, showActionToast]);

  const addContactTags = useCallback(() => {
    closeContactMenu();
    showActionToast("Contact tags will be available when structured contact tags are enabled.", "info");
  }, [closeContactMenu, showActionToast]);

  const archiveContact = useCallback(async () => {
    const contact = rowMenuContact;
    closeContactMenu();
    const contactId = Number(contact?.id || 0);
    if (!Number.isFinite(contactId) || contactId <= 0) return;
    if (!window.confirm(`Archive ${contact?.name || "this contact"}?`)) return;

    try {
      const response = await fetch(`${API_BASE}/api/hiring-managers/${contactId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        credentials: "include",
        body: JSON.stringify({
          name: contact.name,
          title: contact.role || contact.title || "Hiring Manager",
          email: contact.email || "",
          phone: contact.phone || "",
          linkedin_url: contact.linkedin_url || "",
          source: contact.creation_source || "",
          owner: contact.owner === EMPTY_VALUE ? "" : contact.owner || "",
          status: "Do not Contact",
          contact_kind: contact.contact_kind || "contact",
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || "Failed to archive contact.");
      setContactRows((previous) =>
        previous.map((row) =>
          Number(row?.id) === contactId ? { ...row, status: "Do not Contact" } : row
        )
      );
      showActionToast("Contact archived.");
    } catch (error) {
      showActionToast(error?.message || "Failed to archive contact.", "error");
    }
  }, [authHeaders, closeContactMenu, rowMenuContact, showActionToast]);

  const handleOpenRowMenu = useCallback((event, row) => {
    event.stopPropagation?.();
    setRowMenuAnchorEl(event.currentTarget);
    setRowMenuLead(row);
  }, []);

  const handleCloseRowMenu = useCallback(() => {
    setRowMenuAnchorEl(null);
    setRowMenuLead(null);
  }, []);

  const handleViewLeadAction = useCallback(() => {
    const lead = rowMenuLead;
    handleCloseRowMenu();
    if (!lead) return;
    openLeadDetails(lead, "details");
  }, [handleCloseRowMenu, openLeadDetails, rowMenuLead]);

  const handleShareLeadAction = useCallback(async () => {
    const lead = rowMenuLead;
    handleCloseRowMenu();
    if (!lead) return;

    try {
      await createAndCopyPublicShareLink({
        apiBase: API_BASE,
        payload: buildPublicSharePayload("lead", lead),
      });
      showActionToast(SHARE_LINK_COPIED_MESSAGE);
    } catch (error) {
      console.error("Failed to share lead:", error);
      showActionToast(error?.message || "Failed to create share link.", "error");
    }
  }, [handleCloseRowMenu, rowMenuLead, showActionToast]);

  const handleEditLeadAction = useCallback(async () => {
    const lead = rowMenuLead;
    handleCloseRowMenu();
    if (!lead) return;

    const dialogValue = await loadLeadDialogValue(lead);
    setLeadDialogAction("edit");
    setLeadForm(dialogValue);
    setIsAddLeadOpen(true);
  }, [handleCloseRowMenu, loadLeadDialogValue, rowMenuLead]);

  const handleDuplicateLeadAction = useCallback(async () => {
    const lead = rowMenuLead;
    handleCloseRowMenu();
    if (!lead) return;

    const dialogValue = await loadLeadDialogValue(lead, { duplicate: true });
    setLeadDialogAction("duplicate");
    setLeadForm(dialogValue);
    setIsAddLeadOpen(true);
  }, [handleCloseRowMenu, loadLeadDialogValue, rowMenuLead]);

  const handleArchiveLeadAction = useCallback(async () => {
    const lead = rowMenuLead;
    handleCloseRowMenu();
    if (!lead) return;

    const leadId = Number(lead?.id ?? lead?.lead_id ?? lead?.leadId ?? 0);
    if (!Number.isFinite(leadId) || leadId <= 0) return;

    const leadLabel =
      cleanFilterValue(
        lead?.lead_name || lead?.job_title || lead?.name || lead?.raw?.job_title || lead?.raw?.company_name
      ) || "this lead";
    const confirmed = window.confirm(
      `Archive ${leadLabel}? This will soft archive the lead and move it to Do Not Contact.`
    );
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE}/api/leads/${leadId}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        credentials: "include",
        body: JSON.stringify({ status: "Do not contact" }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Failed to archive lead (${response.status})`);
      }

      setRows((prev) =>
        prev.map((row) =>
          Number(row?.id) === leadId ? { ...row, status: "Do not contact" } : row
        )
      );
      setSelectedLeadPreview((prev) =>
        Number(prev?.id ?? prev?.lead_id ?? prev?.leadId) === leadId
          ? { ...prev, status: "Do not contact" }
          : prev
      );
      setSelectedLeadCompany((prev) =>
        Number(prev?.id) === leadId ? { ...prev, status: "Do not contact" } : prev
      );
      showActionToast("Lead archived.");
    } catch (error) {
      console.error("Failed to archive lead:", error);
      showActionToast(error?.message || "Failed to archive lead.", "error");
    }
  }, [authHeaders, handleCloseRowMenu, rowMenuLead, showActionToast]);

  const handleDeleteLeadAction = useCallback(async () => {
    const lead = rowMenuLead;
    handleCloseRowMenu();
    if (!lead || !isAdminUser) return;

    const leadId = Number(lead?.id ?? lead?.lead_id ?? lead?.leadId ?? 0);
    if (!Number.isFinite(leadId) || leadId <= 0) return;

    const leadLabel =
      cleanFilterValue(
        lead?.lead_name || lead?.job_title || lead?.name || lead?.raw?.job_title || lead?.raw?.company_name
      ) || "this lead";
    const confirmed = window.confirm(`Delete ${leadLabel}? This cannot be undone.`);
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE}/api/leads/bulk-delete`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        credentials: "include",
        body: JSON.stringify({ ids: [leadId] }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Failed to delete lead (${response.status})`);
      }
      notifyCompanyRelationshipsChanged();

      setRows((prev) => prev.filter((row) => Number(row?.id) !== leadId));
      setSelectedLeadIds((prev) =>
        (prev || []).filter((value) => Number(value) !== leadId)
      );
      setSelectedLeadPreview((prev) =>
        Number(prev?.id ?? prev?.lead_id ?? prev?.leadId) === leadId ? null : prev
      );
      setSelectedLeadCompany((prev) => (Number(prev?.id) === leadId ? null : prev));
      showActionToast("Lead deleted.");
    } catch (error) {
      console.error("Failed to delete lead:", error);
      showActionToast(error?.message || "Failed to delete lead.", "error");
    }
  }, [authHeaders, handleCloseRowMenu, isAdminUser, rowMenuLead, showActionToast]);

  const handleLeadDialogSubmit = useCallback(
    () => {
      setReloadKey((prev) => prev + 1);
      return leadDialogAction === "edit" ? false : undefined;
    },
    [leadDialogAction]
  );

  useEffect(() => {
    if (detailLeadId) return;
    if (!closingLeadDetailRef.current) return;
    closingLeadDetailRef.current = false;
  }, [detailLeadId]);

  useEffect(() => {
    let active = true;
    const usesDatabaseStatFilter = ["contacts", "sequences", "leadAgents"].includes(activeStatId) ||
      String(activeStatId || "").startsWith("status:");
    const hasLocalFilters = Boolean(advancedApplied) || Object.entries(filtersApplied).some(([key, value]) => {
      if (key === "statuses" && usesDatabaseStatFilter) return false;
      return Array.isArray(value) ? value.length > 0 : Boolean(cleanFilterValue(value));
    });
    const query = new URLSearchParams({
      page: String(hasLocalFilters ? 1 : leadsPage),
      // Lead drawer filters are evaluated locally. Fetch the complete set
      // before paginating so matching rows on later pages are not lost.
      limit: String(hasLocalFilters ? 1000 : leadsRowsPerPage),
      sort: "id",
      order: "desc",
    });
    if (search.trim()) query.set("search", search.trim());
    if (activeStatId && activeStatId !== "total") query.set("stats_filter", activeStatId);

    (hasLocalFilters
      ? loadFilterRecords(`/api/leads?${query}`).then((rows) => ({ rows, total: rows.length }))
      : fetch(`${API_BASE}/api/leads?${query.toString()}`, {
          headers: { ...authHeaders() }, credentials: "include",
        }).then((r) => r.json()))
      .then((data) => {
        if (!active) return;
        const raw = data?.rows || data || [];
        const total = Number(data?.total);
        setLeadTableTotal(Number.isFinite(total) ? Math.max(0, total) : raw.length);
        const mapped = raw.map(mapLeadRowToListRow);
        setRows(mapped);
        backfillLeadLocations(mapped).then((didUpdate) => {
          if (active && didUpdate) {
            setReloadKey((prev) => prev + 1);
          }
        });
      })
      .catch((err) => { if (active) console.error(err); });

    return () => { active = false; };

  }, [activeStatId, advancedApplied, authHeaders, backfillLeadLocations, filtersApplied, leadsPage, leadsRowsPerPage, reloadKey, search]);

  useEffect(() => {
    let active = true;
    fetch(`${API_BASE}/api/leads?summary=stats`, {
      headers: { ...authHeaders() },
      credentials: "include",
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Failed to fetch lead stats (${response.status})`);
        return response.json();
      })
      .then((data) => {
        if (!active) return;
        setLeadStats(data && typeof data === "object" ? data : null);
        const total = Number(data?.total);
        if (Number.isFinite(total)) setLeadsTotal(Math.max(0, total));
      })
      .catch((error) => {
        // Preserve the existing in-memory fallback if an older API is still
        // being deployed while the page is open.
        console.warn("Failed to fetch database-backed lead stats:", error);
        if (active) setLeadStats(null);
      });

    return () => {
      active = false;
    };
  }, [authHeaders, reloadKey]);

  useEffect(() => {
    let active = true;
    const query = buildContactListQuery({ page: contactTablePage, limit: contactTableRowsPerPage, ...contactFilters });
    const contactSearch = listTab === LIST_TABS.contacts ? search.trim() : "";
    if (contactSearch) query.set("search", contactSearch);

    fetch(`${API_BASE}/api/hiring-managers?${query.toString()}`, {
      headers: { ...authHeaders() },
      credentials: "include",
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Failed to fetch contacts (${response.status})`);
        return response.json();
      })
      .then((data) => {
        if (!active) return;
        const raw = Array.isArray(data) ? data : data.rows || [];
        const total = Number(data?.total);
        const resolvedTotal = Number.isFinite(total) ? Math.max(0, total) : raw.length;
        setContactTableTotal(resolvedTotal);
        // Use this same authoritative response for the tab badge, so its
        // value cannot change merely because the user opens another page.
        if (!contactSearch && !contactFilters.activities?.length && !contactFilters.companyNames?.length) setContactRowsTotal(resolvedTotal);
        setContactRows(
          raw.map((row) => ({
            id: row.id,
            name: row.name || "",
            role: normalizeContactRoleLabel(row.role || row.title, {
              contactKind: row.contact_kind,
              source: row.source,
              fallback: "Hiring Manager",
            }),
            source: row.source || EMPTY_VALUE,
            entity_type: row.entity_type || "",
            entity_id: row.entity_id,
            entity_name: row.entity_name || "",
            entity_logo: row.entity_logo || "",
            email: row.email || "",
            phone: row.phone || "",
            linkedin_url: row.linkedin_url || row.linkedin || "",
            profile_image: row.profile_image || "",
            contact_kind: row.contact_kind || "",
            created_at: row.created_at,
            updated_at: row.updated_at,
          }))
        );
      })
      .catch((error) => {
        if (active) console.error("Failed to fetch contacts:", error);
      });

    return () => {
      active = false;
    };
  }, [authHeaders, contactTablePage, contactTableRowsPerPage, listTab, reloadKey, search, contactFilters]);

  useEffect(() => {
    let active = true;

    loadSequenceRows()
      .then((nextRows) => {
        if (!active) return;
        setSequenceRows(nextRows);
      })
      .catch((error) => {
        console.warn("Failed to fetch lead sequences for stats:", error);
        if (!active) return;
        setSequenceRows([]);
      });

    return () => {
      active = false;
    };
  }, [loadSequenceRows, reloadKey]);

  const showingContacts = listTab === LIST_TABS.contacts;
  const showingSalesPipeline = listTab === LIST_TABS.salesPipeline;
  const contactsUseServerPagination = showingContacts && activeStatId === "total";

  const enrichedContactRows = useMemo(() => {
    const leadMap = new Map(rows.map((r) => [String(r.id), r]));
    const companyMap = new Map(
      rows
        .filter((row) => Number.isFinite(Number(row?.company_id)) && Number(row.company_id) > 0)
        .map((row) => [String(Number(row.company_id)), row])
    );
    return contactRows.map((c) => {
      const entityType = normalizeHiringManagerEntityType(c.entity_type);
      const entityId = String(c.entity_id ?? "");

      const entity =
        entityType === "lead"
          ? leadMap.get(entityId)
          : companyMap.get(entityId) || {};
      const entityOwner = entity?.owner_name || entity?.owner || entity?.team_member || "";
      const contactOwner = c?.owner_name || c?.owner || c?.team_member || entityOwner;
      const techStackMeta = buildTechStackMeta(entity || {});
      const contactLastSyncSource = c.updated_at || c.created_at || entity?.last_sync_at || "";

      return {
        ...c,
        entity_type: entityType || c.entity_type || "",
        entity_id: Number.isFinite(Number(c.entity_id)) ? Number(c.entity_id) : c.entity_id,
        entity_name: c.entity_name || entity?.name || "",
        entity_logo: c.entity_logo || entity?.company_logo || "",
        company_id:
          Number.isFinite(Number(entity?.company_id)) && Number(entity?.company_id) > 0
            ? Number(entity.company_id)
            : null,
        status: cleanFilterValue(c?.status || entity?.status || "No stage") || "No stage",
        company: c.entity_name || entity?.name || EMPTY_VALUE,
        company_logo: c.entity_logo || entity?.company_logo || entity?.logo || "",
        company_website:
          entity?.website_url || entity?.website || entity?.company_website || entity?.career_website || "",
        location: cleanFilterValue(entity?.location) || EMPTY_VALUE,
        industry: cleanFilterValue(entity?.industry) || EMPTY_VALUE,
        owner: cleanFilterValue(contactOwner) || EMPTY_VALUE,
        website_url:
          entity?.website_url || entity?.website || entity?.career_website || "",
        creation_source: cleanFilterValue(c.source || entity?.source) || "",
        email:
          cleanFilterValue(c.email) ||
          cleanFilterValue(entity?.company_primary_contact_email) ||
          cleanFilterValue(entity?.job_primary_contact_email) ||
          "",
        phone:
          cleanFilterValue(c.phone) ||
          cleanFilterValue(entity?.company_primary_contact_phone) ||
          cleanFilterValue(entity?.job_primary_contact_phone) ||
          "",
        linkedin_url: cleanFilterValue(c.linkedin_url || entity?.linkedin_url) || "",
        last_activity_type:
          cleanFilterValue(entity?.last_activity_type || entity?.lastActivityType || "") ||
          EMPTY_VALUE,
        last_activity: cleanFilterValue(entity?.last_activity || entity?.lastActivity || "") || EMPTY_VALUE,
        last_sync: formatTimeAgoShort(contactLastSyncSource),
        last_sync_at: toTimestampValue(contactLastSyncSource),
        tech_stack_tokens: techStackMeta.tokens,
        tech_stack_primary: techStackMeta.primary,
        tech_stack_extra_count: techStackMeta.extraCount,
        tech_stack_sort: techStackMeta.sortValue,
      };
    });
  }, [contactRows, rows]);

  const companyContactsByCompanyId = useMemo(() => {
    const next = new Map();

    contactRows.forEach((row) => {
      const entityType = normalizeHiringManagerEntityType(row?.entity_type);
      if (entityType !== "company" && entityType !== "client") return;

      const companyId = Number(row?.entity_id);
      if (!Number.isFinite(companyId) || companyId <= 0) return;

      const key = String(companyId);
      const existing = next.get(key) || [];
      existing.push(row);
      next.set(key, existing);
    });

    return next;
  }, [contactRows]);

  const enrichedLeadRows = useMemo(
    () =>
      rows.map((row) => {
        if (cleanFilterValue(row?.contact_avatar_url)) return row;

        const companyId = Number(row?.company_id);
        if (!Number.isFinite(companyId) || companyId <= 0) return row;

        const avatarUrl = resolveCompanyPrimaryContactAvatar(
          row,
          companyContactsByCompanyId.get(String(companyId)) || []
        );
        if (!avatarUrl) return row;

        return {
          ...row,
          contact_avatar_url: avatarUrl,
        };
      }),
    [companyContactsByCompanyId, rows]
  );

  const leadSequenceRows = useMemo(
    () =>
      sequenceRows.filter((row) =>
        LEAD_SEQUENCE_ENTITY_TYPES.has(normalizeSequenceEntityType(row?.entity_type))
      ),
    [sequenceRows]
  );
  const leadSequenceKeySet = useMemo(() => {
    const next = new Set();

    leadSequenceRows.forEach((row) => {
      buildLeadSequenceEntityKeys(
        row?.entity_type,
        row?.entity_id ?? row?.entityId ?? row?.id
      ).forEach((key) => next.add(key));
    });

    return next;
  }, [leadSequenceRows]);
  const leadsInSequenceCount = useMemo(
    () =>
      enrichedLeadRows.filter((row) => {
        const keys = buildLeadSequenceEntityKeys("lead", row?.id);
        const companyId = Number(row?.company_id);
        if (Number.isFinite(companyId) && companyId > 0) {
          keys.push(...buildLeadSequenceEntityKeys("company", companyId));
        }
        return keys.some((key) => leadSequenceKeySet.has(key));
      }).length,
    [enrichedLeadRows, leadSequenceKeySet]
  );
  const leadContactEntityIdSet = useMemo(() => {
    const next = new Set();
    enrichedContactRows.forEach((row) => {
      if (normalizeHiringManagerEntityType(row?.entity_type) !== "lead") return;
      const entityId = Number(row?.entity_id);
      if (!Number.isFinite(entityId) || entityId <= 0) return;
      next.add(String(entityId));
    });
    return next;
  }, [enrichedContactRows]);
  const companyContactEntityIdSet = useMemo(() => {
    const next = new Set();
    enrichedContactRows.forEach((row) => {
      const entityType = normalizeHiringManagerEntityType(row?.entity_type);
      if (entityType !== "company" && entityType !== "client") return;
      const entityId = Number(row?.entity_id);
      if (!Number.isFinite(entityId) || entityId <= 0) return;
      next.add(String(entityId));
    });
    return next;
  }, [enrichedContactRows]);
  const leadsWithContactsCount = useMemo(
    () =>
      enrichedLeadRows.filter((row) => {
        const rowId = Number(row?.id);
        const companyId = Number(row?.company_id);
        return (
          (Number.isFinite(rowId) && leadContactEntityIdSet.has(String(rowId))) ||
          (Number.isFinite(companyId) &&
            companyId > 0 &&
            companyContactEntityIdSet.has(String(companyId))) ||
          Boolean(cleanFilterValue(row?.contact_name)) ||
          Boolean(cleanFilterValue(row?.email_url)) ||
          Boolean(cleanFilterValue(row?.phone_url))
        );
      }).length,
    [companyContactEntityIdSet, enrichedLeadRows, leadContactEntityIdSet]
  );
  const leadAgentsCount = useMemo(
    () =>
      rows.filter((row) =>
        Boolean(cleanFilterValue(row?.lead_agent)) ||
        hasLeadAgentActivity(row?.last_activity_type || row?.lastActivityType || row?.last_activity)
      ).length,
    [rows]
  );

  const activeRows = showingContacts ? enrichedContactRows : enrichedLeadRows;

  const filterLeadRows = useCallback((activeRows, filtersApplied) => {
    if (showingContacts) {
      let filtered = activeRows;

      if (activeStatId && activeStatId !== "total") {
        const dynamicStatusId = activeStatId.startsWith("status:")
          ? activeStatId.slice(7)
          : "";
        const contactStatMatcher = {
          contacts: () => true,
          sequences: (row) => {
            const keys = buildLeadSequenceEntityKeys(row?.entity_type, row?.entity_id);
            const companyId = Number(row?.company_id);
            if (Number.isFinite(companyId) && companyId > 0) {
              keys.push(...buildLeadSequenceEntityKeys("company", companyId));
            }
            return keys.some((key) => leadSequenceKeySet.has(key));
          },
          leadAgents: (row) =>
            hasLeadAgentActivity(
              row?.last_activity_type || row?.lastActivityType || row?.last_activity
            ),
        };
        const match = dynamicStatusId
          ? (row) => norm(row?.status || "No stage") === dynamicStatusId
          : contactStatMatcher[activeStatId];
        if (match) filtered = filtered.filter((row) => match(row));
      }

      if (!search.trim()) return filtered;
      const q = search.trim().toLowerCase();
      return filtered.filter((r) =>
        [
          r.name,
          r.role,
          r.status,
          r.company,
          r.location,
          r.source,
          r.industry,
          r.tech_stack_sort,
          r.owner,
        ]
          .filter(Boolean)
          .some((v) => v.toString().toLowerCase().includes(q))
      );
    }

    let out = applyCompanyFilters(activeRows, filtersApplied);
    out = applyAdvancedCompanyFilters(out, advancedApplied);

    if (showingSalesPipeline) {
      out = out.filter((row) => SALES_PIPELINE_STAGES.includes(getSalesPipelineStage(row?.status)));
    }

    if (activeStatId && activeStatId !== "total") {
      const byStat = {
        contacts: (row) => {
          const rowId = Number(row?.id);
          const companyId = Number(row?.company_id);
          return (
            (Number.isFinite(rowId) && leadContactEntityIdSet.has(String(rowId))) ||
            (Number.isFinite(companyId) && companyId > 0 && companyContactEntityIdSet.has(String(companyId))) ||
            Boolean(cleanFilterValue(row?.contact_name)) ||
            Boolean(cleanFilterValue(row?.email_url)) ||
            Boolean(cleanFilterValue(row?.phone_url))
          );
        },
        sequences: (row) => {
          const keys = buildLeadSequenceEntityKeys("lead", row?.id);
          const companyId = Number(row?.company_id);
          if (Number.isFinite(companyId) && companyId > 0) {
            keys.push(...buildLeadSequenceEntityKeys("company", companyId));
          }
          return keys.some((key) => leadSequenceKeySet.has(key));
        },
        leadAgents: (row) =>
          hasLeadAgentActivity(
            row?.last_activity_type || row?.lastActivityType || row?.last_activity
          ),
      };
      const dynamicStatusId = activeStatId.startsWith("status:")
        ? activeStatId.slice(7)
        : "";
      const match = dynamicStatusId
        ? (row) => norm(row?.status || "No stage") === dynamicStatusId
        : byStat[activeStatId];
      if (match) out = out.filter((row) => match(row));
    }

    if (!search.trim()) return out;
    const q = search.trim().toLowerCase();
    return out.filter((r) =>
      [
        r.lead_name,
        r.company,
        r.name,
        r.job_title,
        r.website,
        r.status,
        r.location,
        ...extractRowLocations(r),
        r.contact_name,
        r.contact_title,
        r.lead_type,
        r.source,
        r.owner,
        r.lead_agent,
        r.industry,
        r.hard_skills,
        r.soft_skills,
        r.technology_stack,
        r.recruitment_tools,
        r.skills,
        r.salary_range,
        r.compensation,
      ]
        .filter(Boolean)
        .some((v) => v.toString().toLowerCase().includes(q))
    );
  }, [
    activeStatId,
    advancedApplied,
    companyContactEntityIdSet,
    leadContactEntityIdSet,
    leadSequenceKeySet,
    search,
    showingSalesPipeline,
    showingContacts,
  ]);

  const filteredRows = useMemo(() => filterLeadRows(activeRows, filtersApplied),
    [filterLeadRows, activeRows, filtersApplied]);

  const topStats = useMemo(() => {
    const dbCount = (key, fallback) => {
      const value = Number(leadStats?.[key]);
      return Number.isFinite(value) && value >= 0 ? value : fallback;
    };

    return (showingSalesPipeline ? SALES_STATS_SLIDES : LEAD_STATS_SLIDES).map((item) => {
      if (item.id === "total") {
        return { ...item, value: String(dbCount("total", leadsTotal)) };
      }
      if (item.id === "contacts") {
        return { ...item, value: String(dbCount("contacts", leadsWithContactsCount)) };
      }
      if (item.id === "sequences") {
        return { ...item, value: String(dbCount("sequences", leadsInSequenceCount)) };
      }
      if (item.id === "leadAgents") {
        return { ...item, value: String(dbCount("leadAgents", leadAgentsCount)) };
      }
      if (item.id.startsWith("status:")) {
        const statusKey = item.id.slice(7);
        const palette = getSalesStatusColors(statusKey);
        return {
          ...item,
          color: palette?.color,
          activeBackground: palette?.activeBackground,
          disabledColor: SALES_STATUS_DISABLED.color,
          disabledBorder: SALES_STATUS_DISABLED.border,
          value: String(
            dbCount(
              statusKey === "client" ? "clients" : statusKey,
              rows.filter((row) => norm(row?.status || "No stage") === statusKey).length
            )
          ),
        };
      }
      return { ...item, value: "0" };
    });
  }, [leadAgentsCount, leadStats, leadsInSequenceCount, leadsTotal, leadsWithContactsCount, rows, showingSalesPipeline]);
  const companyAdvancedSearchOptions = useMemo(
    () => ({
      locations: buildLocationOptions(rows),
      statuses: LEAD_STATUS_VALUES,
    }),
    [rows]
  );
  const leadDetailFieldOptions = useMemo(() => buildDetailFieldOptions(rows), [rows]);
  const tableResetSignal = useMemo(
    () =>
      JSON.stringify({
        listTab,
        search,
        activeStatId,
        filtersApplied,
        advancedApplied,
      }),
    [activeStatId, advancedApplied, filtersApplied, listTab, search]
  );
  const usesDatabaseStatFilter = ["contacts", "sequences", "leadAgents"].includes(activeStatId) ||
    String(activeStatId || "").startsWith("status:");
  const hasLocalLeadFilters = useMemo(
    () =>
      Object.entries(filtersApplied).some(([key, value]) => {
        // A funnel route mirrors its single status in activeStatId, which the
        // API already applies. It is not a separate client-only filter.
        if (key === "statuses" && usesDatabaseStatFilter) return false;
        return Array.isArray(value) ? value.length > 0 : Boolean(cleanFilterValue(value));
      }),
    [filtersApplied, usesDatabaseStatFilter]
  );

  const handleListTabChange = useCallback(
    (nextValue) => {
      const nextTab = Object.values(LIST_TABS).includes(nextValue)
        ? nextValue
        : LIST_TABS.leads;
      const params = new URLSearchParams(location.search);

      if (nextTab === LIST_TABS.salesPipeline) {
        params.set("view", LIST_TABS.salesPipeline);
      } else {
        params.delete("view");
        params.delete("stage");
        params.delete("overviewMetric");
      }

      setListTab(nextTab);
      if (nextTab === LIST_TABS.contacts) setContactTablePage(1);
      setSelectedLeadIds([]);
      setSelectedContactIds([]);
      navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : "",
        },
        { replace: true }
      );
    },
    [location.pathname, location.search, navigate]
  );

  const handleLeadsPageChange = useCallback((nextPage) => {
    setLeadsPage(Math.max(1, Number(nextPage) + 1));
  }, []);

  const handleLeadsRowsPerPageChange = useCallback((nextRowsPerPage) => {
    setLeadsRowsPerPage(Math.max(1, Number(nextRowsPerPage) || 10));
    setLeadsPage(1);
  }, []);

  const handleContactTablePageChange = useCallback((nextPage) => {
    setContactTablePage(Math.max(1, Number(nextPage) + 1));
  }, []);

  const handleContactTableRowsPerPageChange = useCallback((nextRowsPerPage) => {
    setContactTableRowsPerPage(Math.max(1, Number(nextRowsPerPage) || 10));
    setContactTablePage(1);
  }, []);

  const handleStatItemClick = useCallback(
    (item) => {
      const nextId = String(item?.id || "");
      const params = new URLSearchParams(location.search);

      if (params.has("stage")) {
        params.delete("stage");
        setFiltersDraft((prev) => ({ ...prev, statuses: [] }));
        setFiltersApplied((prev) => ({ ...prev, statuses: [] }));
        navigate(
          {
            pathname: location.pathname,
            search: params.toString() ? `?${params.toString()}` : "",
          },
          { replace: true }
        );
      }

      if (!nextId || nextId === "total") {
        setActiveStatId("total");
        return;
      }
      setActiveStatId((prev) => (prev === nextId ? "total" : nextId));
    },
    [location.pathname, location.search, navigate]
  );

  const updateAppliedFiltersFromResults = useCallback((updater) => {
    setFiltersApplied((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      setFiltersDraft(next);
      return next;
    });
    setLeadsPage(1);
  }, []);

  const handleRemoveFilterPill = useCallback(
    (field, value) => {
      updateAppliedFiltersFromResults((prev) => {
        if (field === "ai") {
          return { ...prev, ai: "" };
        }

        return {
          ...prev,
          [field]: removeFilterValueFromList(prev[field], value),
        };
      });
    },
    [updateAppliedFiltersFromResults]
  );

  const resultsFilterPills = useMemo(() => {
    const pills = [];

    const appendTextPill = (field, value, prefix = "") => {
      const label = cleanFilterValue(value);
      if (!label) return;

      pills.push({
        key: `${field}:${norm(label)}`,
        label: `${prefix}${label}`,
        onDelete: () => handleRemoveFilterPill(field, label),
      });
    };

    const appendArrayPills = (field, values = []) => {
      (values || []).forEach((value) => {
        const label = cleanFilterValue(value);
        if (!label) return;

        pills.push({
          key: `${field}:${norm(label)}`,
          label,
          onDelete: () => handleRemoveFilterPill(field, label),
        });
      });
    };

    appendTextPill("ai", filtersApplied.ai, "AI: ");
    appendArrayPills("companyNames", filtersApplied.companyNames);
    appendArrayPills("techKeywords", filtersApplied.techKeywords);
    appendArrayPills("locations", filtersApplied.locations);
    appendArrayPills("owners", filtersApplied.owners);
    appendArrayPills("statuses", filtersApplied.statuses);
    appendArrayPills("activities", filtersApplied.activities);
    appendArrayPills("sources", filtersApplied.sources);

    return pills;
  }, [filtersApplied, handleRemoveFilterPill]);

  const selectedLeadIdSet = useMemo(
    () => new Set((selectedLeadIds || []).map((id) => String(id))),
    [selectedLeadIds]
  );
  const selectedLeadRows = useMemo(
    () => filteredRows.filter((row) => selectedLeadIdSet.has(String(row?.id))),
    [filteredRows, selectedLeadIdSet]
  );
  const selectedSequenceItems = useMemo(
    () => selectedLeadRows.map((row) => buildLeadSequenceItem(row, "leads")),
    [selectedLeadRows]
  );
  const selectedContactIdSet = useMemo(
    () => new Set((selectedContactIds || []).map((id) => String(id))),
    [selectedContactIds]
  );
  const selectedContactRows = useMemo(
    () => filteredRows.filter((row) => selectedContactIdSet.has(String(row?.id))),
    [filteredRows, selectedContactIdSet]
  );
  const selectedContactSequenceItems = useMemo(
    () =>
      selectedContactRows.map((row) =>
        buildHiringManagerSequenceItem(row, "leads-hiring-managers")
      ),
    [selectedContactRows]
  );

  const openFilters = () => {
    setFiltersDraft(filtersApplied);
    setIsFilterOpen(true);
  };

  const clearFilters = () => {
    setFiltersDraft(DEFAULT_FILTERS);
    setFiltersApplied(DEFAULT_FILTERS);
    setLeadsPage(1);
  };

  const applyFilters = (next) => {
    const finalFilters = next || filtersDraft;
    setFiltersDraft(finalFilters);
    setFiltersApplied(finalFilters);
    setLeadsPage(1);
    setIsFilterOpen(false);
  };

  const openAdvancedSearch = (drawerFilters) => {
    const nextFilters = drawerFilters || filtersDraft;
    setFiltersDraft(nextFilters);
    setFiltersApplied(nextFilters);
    setLeadsPage(1);
    setIsFilterOpen(false);
    setShowAdvanced(true);
  };

  const closeAdvancedSearch = () => setShowAdvanced(false);

  const applyAdvancedSearch = (advFilters) => {
    setAdvancedApplied(advFilters);
    setLeadsPage(1);
    setShowAdvanced(false);
  };

  const openAddLeadDialog = () => {
    setLeadDialogAction("create");
    setLeadForm(null);
    setIsAddLeadOpen(true);
  };

  const handleCloseLeadDialog = useCallback(() => {
    setIsAddLeadOpen(false);
    setLeadForm(null);
    setLeadDialogAction("create");
  }, []);

  const navigateToContentTab = useCallback(
    (tabId) => {
      const nextTabId = cleanFilterValue(tabId);
      if (!nextTabId) return;
      navigate(`/content?tab=${encodeURIComponent(nextTabId)}`);
    },
    [navigate]
  );

  const handleOpenAddToSequence = useCallback(() => {
    setIsAddToSequenceOpen(true);
  }, []);

  const handleContactLead = useCallback(() => {
    if (!selectedLeadRows.length) {
      showActionToast("Select at least one lead first.", "info");
      return;
    }

    setIsAddToSequenceOpen(true);
  }, [selectedLeadRows.length, showActionToast]);

  const handleLeadSequenceCreated = useCallback(() => {
    setSelectedLeadIds([]);
    setIsAddToSequenceOpen(false);
    navigateToContentTab("sequences");
  }, [navigateToContentTab]);

  const handleCloseAgentDialog = useCallback(() => {
    const nextContentTab = pendingAgentContentTab;
    setIsAgentDialogOpen(false);
    setAgentDialogInitialPrompt("");
    setAgentDialogSubmitOnOpen(false);
    setPendingAgentContentTab("");

    if (nextContentTab) {
      navigateToContentTab(nextContentTab);
    }
  }, [navigateToContentTab, pendingAgentContentTab]);

  const openLeadAgentDialog = useCallback(
    ({ taskValue = "", contentTabOnClose = "", successMessage = "" } = {}) => {
      const rowsToProcess = selectedLeadRows.filter(Boolean);

      setAgentDialogInitialPrompt(
        buildLeadAgentTaskPrompt({
          taskValue,
          rows: rowsToProcess,
        })
      );
      setAgentDialogSubmitOnOpen(true);
      setPendingAgentContentTab(cleanFilterValue(contentTabOnClose));
      setIsAgentDialogOpen(true);

      if (successMessage) {
        showActionToast(successMessage);
      }
    },
    [selectedLeadRows, showActionToast]
  );

  const handleOpenHiringManagerSequence = useCallback(() => {
    if (!selectedContactRows.length) {
      window.alert("Select at least one hiring manager first.");
      return;
    }
    setIsAddToSequenceOpen(true);
  }, [selectedContactRows.length]);

  const handleExportHiringManagers = useCallback(() => {
    const exportRows = selectedContactRows.length ? selectedContactRows : filteredRows;
    if (!exportRows.length) {
      window.alert("No hiring managers available to export.");
      return;
    }

    const header = [
      "Name",
      "Role",
      "Status",
      "Company",
      "Location",
      "Source",
      "Industry",
      "Tech Stack",
      "Last Sync",
      "Team Member",
    ];
    const lines = exportRows.map((row) =>
      [
        row.name,
        row.role,
        row.status,
        row.company,
        row.location,
        row.source,
        row.industry,
        row.tech_stack_tokens?.join(" | ") || "",
        row.last_sync,
        row.owner,
      ]
        .map(toCsvValue)
        .join(",")
    );

    const blob = new Blob([[header.map(toCsvValue).join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `hiring-managers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 0);
  }, [filteredRows, selectedContactRows]);

  const handleExportSelectedLeads = useCallback(() => {
    const exportRows = selectedLeadRows.length ? selectedLeadRows : filteredRows;
    if (!exportRows.length) {
      showActionToast("No leads available to export.", "info");
      return;
    }

    const header = [
      "Name",
      "Status",
      "Company",
      "Contact",
      "Type",
      "Source",
      "Location",
      "Contact Information",
      "Last Activity",
      "Owner",
      "Lead Agent",
    ];
    const lines = exportRows.map((row) =>
      [
        row.lead_name || row.job_title || row.name,
        row.status,
        row.company || row.name,
        [row.contact_name, row.contact_title || row.contact_role]
          .filter((value) => Boolean(cleanFilterValue(value)))
          .join(" | "),
        row.lead_type,
        row.source,
        row.location,
        [row.website_url, row.email_url, row.phone_url, row.linkedin_url]
          .filter((value) => Boolean(cleanFilterValue(value)))
          .join(" | "),
        row.last_activity,
        row.owner,
        row.lead_agent,
      ]
        .map(toCsvValue)
        .join(",")
    );

    const blob = new Blob([[header.map(toCsvValue).join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 0);

    showActionToast(
      `Exported ${exportRows.length} lead${exportRows.length === 1 ? "" : "s"}.`
    );
  }, [filteredRows, selectedLeadRows, showActionToast]);

  const handleDeleteHiringManagers = useCallback((_event, options = {}) => {
    if (!selectedContactRows.length) {
      window.alert("Select at least one hiring manager first.");
      return;
    }

    if (!options?.confirmedByBulkActionsBar) {
      const confirmed = window.confirm(
        `Delete ${selectedContactRows.length} hiring manager(s)? This cannot be undone.`
      );
      if (!confirmed) return;
    }

    const idSet = new Set(selectedContactRows.map((row) => String(row?.id)));
    setContactRows((prev) => prev.filter((row) => !idSet.has(String(row?.id))));
    setSelectedContactIds([]);
  }, [selectedContactRows]);

  const handleBulkHiringManagerStatusChange = useCallback(
    (status) => {
      const nextStatus = cleanFilterValue(status);
      if (!nextStatus) return;
      if (!selectedContactRows.length) {
        window.alert("Select at least one hiring manager first.");
        return;
      }

      const leadIdSet = new Set();
      const companyIdSet = new Set();

      selectedContactRows.forEach((row) => {
        const entityType = normalizeHiringManagerEntityType(row?.entity_type);
        const entityId = Number(row?.entity_id);
        if (!Number.isFinite(entityId) || entityId <= 0) return;

        if (entityType === "lead") {
          leadIdSet.add(entityId);
          return;
        }

        if (entityType === "company" || entityType === "client") {
          companyIdSet.add(entityId);
        }
      });

      setRows((prev) =>
        prev.map((row) => {
          const leadId = Number(row?.id);
          const companyId = Number(row?.company_id);
          const shouldUpdateLead = Number.isFinite(leadId) && leadIdSet.has(leadId);
          const shouldUpdateCompany =
            Number.isFinite(companyId) && companyIdSet.has(companyId);

          return shouldUpdateLead || shouldUpdateCompany
            ? { ...row, status: nextStatus }
            : row;
        })
      );
    },
    [selectedContactRows]
  );

  const handleAssignTeamMember = useCallback(
    async (member) => {
      const ids = selectedLeadRows
        .map((row) => Number(row?.id))
        .filter((value) => Number.isFinite(value) && value > 0);

      if (!ids.length) return;

      const res = await fetch(`${API_BASE}/api/leads/bulk-assign-team-member`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        credentials: "include",
        body: JSON.stringify({
          ids,
          assignee_user_id: member?.id ?? null,
          assignee_name: member?.name || "",
        }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Failed to assign team member (${res.status})`);
      }

      const idSet = new Set(ids.map((value) => String(value)));
      setRows((prev) =>
        prev.map((row) =>
          idSet.has(String(row?.id))
            ? {
                ...row,
                owner: member?.name || row.owner,
              }
            : row
        )
      );
    },
    [authHeaders, selectedLeadRows]
  );

  const handleAssignOwner = useCallback(
    async (member) => {
      await handleAssignTeamMember(member);
    },
    [handleAssignTeamMember]
  );

  const handleBulkDelete = async (_event, options = {}) => {
    if (!isAdminUser) {
      showActionToast("Only admin users can delete leads.", "error");
      return;
    }

    if (!selectedLeadIds.length) return;

    if (!options?.confirmedByBulkActionsBar) {
      const confirmed = window.confirm(
        `Delete ${selectedLeadIds.length} lead(s)? This cannot be undone.`
      );
      if (!confirmed) return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/leads/bulk-delete`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        credentials: "include",
        body: JSON.stringify({ ids: selectedLeadIds }),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || "Failed to delete leads");
      }
      notifyCompanyRelationshipsChanged();

      const idsSet = new Set(selectedLeadIds.map((x) => Number(x)));
      setRows((prev) => prev.filter((r) => !idsSet.has(Number(r.id))));
      setSelectedLeadIds([]);
      showActionToast(
        `Deleted ${idsSet.size} lead${idsSet.size === 1 ? "" : "s"}.`
      );
    } catch (err) {
      console.error(err);
      showActionToast("Delete failed. Check console for details.", "error");
    }
  };

  const handleBulkStatusChange = useCallback(
    async (status) => {
      const nextStatus = String(status || "").trim();
      if (!nextStatus || !selectedLeadRows.length || statusSaving) return;

      const ids = selectedLeadRows
        .map((row) => Number(row?.id))
        .filter((value) => Number.isFinite(value) && value > 0);

      if (!ids.length) return;

      setStatusSaving(true);

      try {
        const res = await fetch(`${API_BASE}/api/leads/bulk-status`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
          },
          credentials: "include",
          body: JSON.stringify({ ids, status: nextStatus }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(payload?.error || `Failed to update lead status (${res.status})`);
        }

        const idSet = new Set(ids.map((id) => String(id)));
        setRows((prev) =>
          prev.map((row) => (idSet.has(String(row?.id)) ? { ...row, status: nextStatus } : row))
        );
        setSelectedLeadIds([]);
        // Keep filtered rows and their count in sync with the server without
        // replacing the current table view.
        setReloadKey((prev) => prev + 1);
        setStatusToast({
          open: true,
          severity: "success",
          message: `Updated ${ids.length} lead${ids.length === 1 ? "" : "s"} to "${nextStatus}".`,
        });
      } catch (error) {
        console.error("Failed to update lead statuses:", error);
        setStatusToast({
          open: true,
          severity: "error",
          message: error?.message || "Failed to update the selected lead statuses.",
        });
      } finally {
        setStatusSaving(false);
      }
    },
    [authHeaders, selectedLeadRows, statusSaving]
  );

  const handleLaunchLeadAgent = useCallback(() => {
    openLeadAgentDialog({
      taskValue: "launchAgent",
      successMessage: `Launching agent for ${selectedLeadRows.length} selected ${
        selectedLeadRows.length === 1 ? "lead" : "leads"
      }.`,
    });
  }, [openLeadAgentDialog, selectedLeadRows.length]);

  const handleCreateLeadProposal = useCallback(() => {
    openLeadAgentDialog({
      taskValue: "createProposal",
      contentTabOnClose: "proposals",
      successMessage:
        "Generating proposal draft. Close the agent to continue in Content -> Proposals.",
    });
  }, [openLeadAgentDialog]);

  const handleOpenTopBarLeadAgent = useCallback(() => {
    setAgentDialogInitialPrompt("");
    setAgentDialogSubmitOnOpen(false);
    setPendingAgentContentTab("");
    setIsAgentDialogOpen(true);
  }, []);

  const handleArchiveSelectedLeads = useCallback(async () => {
    await handleBulkStatusChange("Do not contact");
  }, [handleBulkStatusChange]);

  const headCellsContacts = useMemo(
    () => [
      { id: "name", label: "Name", numeric: false, minWidth: 190 },
      { id: "company", label: "Company Name", numeric: false, minWidth: 190 },
      { id: "status", label: "Status", numeric: false, minWidth: 118 },
      { id: "role", label: "Role", numeric: false, minWidth: 160 },
      { id: "owner", label: "Source", numeric: false, minWidth: 130 },
      { id: "info", label: "Contact information", numeric: false, sortable: false, minWidth: 120 },
      { id: "actions", label: "Actions", numeric: false, sortable: false, minWidth: 64 },
    ],
    []
  );

  const renderContactRow = (row) => (
    <>
      <TableCell sx={{ minWidth: 190 }}>
        <Box
          display="flex"
          alignItems="center"
          gap={1.5}
          sx={{ minWidth: 0, cursor: "pointer" }}
          onClick={() =>
            navigate(`/contacts/${encodeURIComponent(String(row.id))}`, {
              state: { contact: row, companyName: row.company || row.entity_name },
            })
          }
        >
          <Avatar
            src={row.profile_image || undefined}
            sx={{
              width: 30,
              height: 30,
              fontSize: 14,
              background: row.profile_image
                ? "transparent"
                : "linear-gradient(135deg,#F4EBFF,#FCE7F3)",
              color: "#7F56D9",
            }}
          >
            {!row.profile_image ? getAvatarLetters(row.name, "H") : null}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{ color: "#344054", lineHeight: 1.2 }}
              noWrap
            >
              {row.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.35,
                width: 200,
                maxWidth: 200,
                color: "#98A2B3",
                lineHeight: 1.2,
              }}
              noWrap
            >
              {row.role || "Hiring Manager"}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell sx={{ minWidth: 190 }}><CompanyTableCell row={row} website={row.company_website || row.website_url} /></TableCell>
      <TableCell sx={{ minWidth: 118 }}>
        <Chip label={row.status || "No stage"} size="small" sx={getHiringManagerStatusChipSx(row.status)} />
      </TableCell>
      <TableCell sx={{ minWidth: 160 }}>
        <Typography variant="body2" sx={{ color: "#475467" }} noWrap>
          {row.role || row.title || "Hiring Manager"}
        </Typography>
      </TableCell>
      <TableCell sx={{ minWidth: 130 }}>
        <Typography variant="body2" sx={{ color: "#475467" }} noWrap>
          {row.owner || EMPTY_VALUE}
        </Typography>
      </TableCell>
      <TableCell sx={{ minWidth: 120 }}>
        <InfoActionIcons
          showDisabled={true}
          showGroupDivider={false}
          preserveOrder={true}
          gap={0.8}
          iconSize={18}
          actions={[
            ...(hasRenderableActionHref("linkedin", row.linkedin_url)
              ? [{ type: "linkedin", href: row.linkedin_url }]
              : []),
            { type: "email", href: row.email },
            { type: "phone", href: row.phone },
          ]}
        />
      </TableCell>
      <TableCell align="center" sx={{ minWidth: 64 }}>
        <IconButton
          size="small"
          aria-label={`Open actions for ${row?.name || "contact"}`}
          sx={{ color: "#98A2B3" }}
          onClick={(event) => openContactMenu(event, row)}
        >
          <MoreVertIcon sx={{ fontSize: 18, color: "currentColor" }} />
        </IconButton>
      </TableCell>
    </>
  );

  const renderLeadRow = (row) => (
    <>
      <TableCell sx={{ minWidth: 100 }}>
        <Chip
          label={showingSalesPipeline ? getSalesPipelineStage(row.status) : row.status || "No stage"}
          size="small"
          sx={getStatusChipSx(row.status)}
        />
      </TableCell>

      <TableCell sx={{ width: 150, minWidth: 150, maxWidth: 150 }}>
        <Typography
          variant="subtitle2"
          fontWeight={600}
          sx={{ color: "#344054", cursor: "pointer" }}
          noWrap
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            openLeadPreview(row);
          }}
        >
          {getLeadJobTitle(row)}
        </Typography>
      </TableCell>


      <TableCell sx={{ minWidth: 180 }}>
        {cleanFilterValue(row.contact_name) && row.contact_name !== EMPTY_VALUE ? (
          <Box
            sx={{ minWidth: 0, cursor: "pointer" }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              openHiringManagerDrawer(row);
            }}
          >
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{ color: "#344054", lineHeight: 1.25 }}
              noWrap
            >
              {row.contact_name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                color: "#98A2B3",
                lineHeight: 1.2,
                mt: 0.35,
                width: 180,
                maxWidth: 180,
              }}
              noWrap
            >
              {row.contact_email || row.contact_title || row.contact_role || "Primary Contact"}
            </Typography>
          </Box>
        ) : (
          EMPTY_VALUE
        )}
      </TableCell>
      <TableCell sx={{ minWidth: 150 }}><CompanyTableCell row={row} name={row.company || row.name} website={row.company_website || row.website_url} onClick={(event) => { event.preventDefault(); event.stopPropagation(); openLeadCompanyDrawer(row); }} /></TableCell>
      <TableCell sx={{ minWidth: 120 }}>
        <Typography variant="body2" sx={{ color: "#475467" }}>
          {row.lead_type || "Manual"}
        </Typography>
      </TableCell>
      <TableCell sx={{ minWidth: 110 }}>
        <Typography variant="body2" sx={{ color: "#475467" }}>
          {row.source || EMPTY_VALUE}
        </Typography>
      </TableCell>
      <TableCell sx={{ minWidth: 160 }}>
        <Typography variant="body2" sx={{ color: "#475467" }} noWrap>
          {row.location || EMPTY_VALUE}
        </Typography>
      </TableCell>

      <TableCell sx={{ minWidth: 120 }}>
        <InfoActionIcons
          showDisabled={true}
          showGroupDivider={false}
          preserveOrder={true}
          gap={0.55}
          iconSize={18}
          actions={buildTableInfoActions([
            { type: "website", href: row.website_url },
            { type: "email", href: row.email_url },
            { type: "phone", href: row.phone_url },
            { type: "linkedin", href: row.linkedin_url },
            { type: "facebook", href: row.facebook_url },
            { type: "twitter", href: row.twitter_url },
          ])}
        />
      </TableCell>

      <TableCell sx={{ minWidth: 96 }}>
        <Typography variant="body2" sx={{ color: "#667085" }} noWrap>
          {formatTimeAgoShort(
            row.last_activity_at ||
              row.last_activity_time ||
              row.lastActivityAt ||
              row.lastActivity ||
              row.last_activity
          )}
        </Typography>
      </TableCell>
      <TableCell sx={{ minWidth: 118 }}>
        {cleanFilterValue(row.owner) && row.owner !== EMPTY_VALUE ? (
          <Box display="flex" alignItems="center" gap={1.1} sx={{ minWidth: 0 }}>
            <Avatar
              sx={{
                width: 28,
                height: 28,
                fontSize: 11,
                fontWeight: 700,
                backgroundColor: "#F4EBFF",
                color: "#7F56D9",
              }}
            >
              {getAvatarLetters(row.owner, "O")}
            </Avatar>
            <Typography variant="body2" sx={{ color: "#475467" }} noWrap>
              {row.owner}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: "#98A2B3" }}>
            {EMPTY_VALUE}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ minWidth: 110 }}>
        {cleanFilterValue(row.lead_agent) ? (
          <Chip
            label={row.lead_agent}
            size="small"
            sx={getMetaChipSx(row.lead_agent, {}, LEAD_AGENT_TAG_SX)}
          />
        ) : (
          <Typography variant="body2" sx={{ color: "#98A2B3" }}>
            {EMPTY_VALUE}
          </Typography>
        )}
      </TableCell>
      <TableCell align="center" sx={{ minWidth: 64 }}>
        <IconButton
          size="small"
          aria-label={`Open actions for ${row?.lead_name || row?.job_title || row?.name || "lead"}`}
          sx={{ color: "#98A2B3" }}
          onClick={(event) => handleOpenRowMenu(event, row)}
        >
          <MoreVertIcon sx={{ fontSize: 18, color: "currentColor" }} />
        </IconButton>
      </TableCell>
    </>
  );

  const topBarActions = [
    {
      key: "filter",
      label: "Filter by",
      variant: "contained",
      startIcon: <Box component="span" aria-hidden="true" sx={{ width: 20, height: 20, backgroundColor: "currentColor", mask: `url(${leadFilterIcon}) center / contain no-repeat`, WebkitMask: `url(${leadFilterIcon}) center / contain no-repeat` }} />,
      onClick: openFilters,
    },
    {
      key: "import",
      label: "Import CSV",
      variant: "contained",
      startIcon: <Box component="span" aria-hidden="true" sx={{ width: 20, height: 20, backgroundColor: "currentColor", mask: `url(${leadImportIcon}) center / contain no-repeat`, WebkitMask: `url(${leadImportIcon}) center / contain no-repeat` }} />,
      onClick: () => setIsCsvImportOpen(true),
    },
    {
      key: "add",
      label: "Add new lead",
      variant: "contained",
      startIcon: <Box component="span" aria-hidden="true" sx={{ width: 20, height: 20, backgroundColor: "currentColor", mask: `url(${leadAddIcon}) center / contain no-repeat`, WebkitMask: `url(${leadAddIcon}) center / contain no-repeat` }} />,
      onClick: openAddLeadDialog,
    },
    {
      key: "add-agent",
      label: "Add Agent",
      variant: "contained",
      startIcon: <Box component="span" aria-hidden="true" sx={{ width: 20, height: 20, backgroundColor: "currentColor", mask: `url(${leadAgentIcon}) center / contain no-repeat`, WebkitMask: `url(${leadAgentIcon}) center / contain no-repeat` }} />,
      onClick: handleOpenTopBarLeadAgent,
    },
  ];

  const bulkNode = (
    <BulkActionsLeadsBar
      selectedCount={selectedCount}
      onAddToSequence={handleOpenAddToSequence}
      onContactLead={handleContactLead}
      onLaunchAgent={handleLaunchLeadAgent}
      onCreateProposal={handleCreateLeadProposal}
      onExportCsv={handleExportSelectedLeads}
      onArchive={handleArchiveSelectedLeads}
      onDelete={handleBulkDelete}
      onChangeStatus={handleBulkStatusChange}
      statusOptions={LEAD_BULK_STATUS_MENU_OPTIONS}
      statusSaving={statusSaving}
      statusFeedback={statusToast}
      onStatusFeedbackClose={() => setStatusToast((prev) => ({ ...prev, open: false }))}
      disabledDelete={!selectedCount}
      showDelete={isAdminUser}
      assignOwnerActionNode={{
        type: "node",
        key: "assign-owner",
        node: (
          <AssignTeamMemberAction
            apiBase={API_BASE}
            selectedCount={selectedCount}
            label="Assign Owner"
            optionsMode="recruiterUsers"
            assignmentRoleLabel="owner"
            onAssign={handleAssignOwner}
          />
        ),
      }}
    />
  );

  const contactBulkNode = useMemo(
    () => (
      <SharedBulkActionsBar
        selectedCount={selectedContactRows.length}
        selectionLabel="hiring manager"
        actions={[
          {
            type: "menu",
            label: "Change Status",
            options: LEAD_BULK_STATUS_MENU_OPTIONS,
            onSelect: handleBulkHiringManagerStatusChange,
          },
          { type: "divider" },
          {
            type: "button",
            label: "Delete",
            onClick: handleDeleteHiringManagers,
          },
          { type: "divider" },
          {
            type: "button",
            label: "Export",
            onClick: handleExportHiringManagers,
          },
        ]}
        rightButton={{
          label: "Sequence",
          confirm: true,
          background: "var(--primary-color)",
          onClick: handleOpenHiringManagerSequence,
          sx: {
            height: 28,
            px: 1.7,
            fontSize: 10.5,
          },
        }}
      />
    ),
    [
      handleBulkHiringManagerStatusChange,
      handleDeleteHiringManagers,
      handleExportHiringManagers,
      handleOpenHiringManagerSequence,
      selectedContactRows.length,
    ]
  );

  if (loading) return <p>Loading...</p>;
  if (user?.role === "candidate") return null;

  if (detailLeadId) {
    return (
      <Box sx={{ py: 0, pb: 3, border: 0 }}>
        <Breadcrumb
          activeLabel={detailLeadLabel}
          appendActiveLabel
          items={
            detailOriginContext
              ? [
                  { label: "Overview", onClick: () => navigate("/overview") },
                  { label: "Companies", path: "/companies" },
                  ...(detailOriginCompanyLabel
                    ? [{ label: detailOriginCompanyLabel, onClick: openOriginCompany }]
                    : []),
                  ...(detailOriginParentTabLabel
                    ? [{ label: detailOriginParentTabLabel, onClick: openOriginCompany }]
                    : []),
                ]
              : [
                  { label: "Overview", onClick: () => navigate("/overview") },
                  { label: "Leads", path: "/leads" },
                ]
          }
        />

        <Box>
          <LeadDetailsView
            key={String(detailLeadId)}
            lead={detailLead}
            user={user}
            detailFieldOptions={leadDetailFieldOptions}
            initialTab={detailTab}
            onTabChange={setActiveDetailTab}
            onResolvedTitleChange={setDetailLeadResolvedTitle}
            onLeadChange={handleDetailLeadChange}
          />
        </Box>

        <LeadDescriptionDialog
          open={isAddLeadOpen}
          onClose={handleCloseLeadDialog}
          value={leadForm}
          onChange={setLeadForm}
          initialScreen={leadDialogInitialScreen}
          titleOverride={leadDialogTitleOverride}
          submitLabel={leadDialogSubmitLabel}
          onSubmit={handleLeadDialogSubmit}
        />

        <AddToSequenceDialog
          open={isAddToSequenceOpen}
          onClose={() => setIsAddToSequenceOpen(false)}
          entityType="lead"
          items={selectedSequenceItems}
          user={user}
          onCreated={handleLeadSequenceCreated}
        />

        <AIAgentDialog
          open={isAgentDialogOpen}
          onClose={handleCloseAgentDialog}
          user={user}
          initialPrompt={agentDialogInitialPrompt}
          submitOnOpen={agentDialogSubmitOnOpen}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 0, pb: 3, border: 0 }}>
      {showAdvanced ? (
        <>
          <Breadcrumb
            items={[
              { label: "Overview", onClick: () => navigate("/overview") },
              { label: "Leads", path: "/leads" },
              { label: "Advance Search" },
            ]}
          />

          <Box>
            <CompanyAdvancedSearchView
              onClose={closeAdvancedSearch}
              onApply={(filters) => applyAdvancedSearch(filters)}
              title="Companies - Advance Search"
              initialFilters={advancedApplied || undefined}
              options={companyAdvancedSearchOptions}
            />
          </Box>
        </>
      ) : (
        <ListTableTabsTemplate
          breadcrumb={
            <Breadcrumb
              activeLabel={
                showingContacts
                  ? CONTACTS_TAB_LABEL
                  : showingSalesPipeline
                    ? "Sales Pipeline"
                    : "Leads"
              }
              items={[
                { label: "Overview", onClick: () => navigate("/overview") },
                { label: "Leads", path: "/leads" },
              ]}
            />
          }
          statsSection={
            <StatsSlider
              items={topStats}
              autoPlay={false}
              showArrows={true}
              cardMinWidth={200}
              gap={24}
              onItemClick={handleStatItemClick}
              activeItemId={activeStatId}
            />
          }
          leftColumnSx={{
            overflow: "visible",
          }}
          contentSection={
            <Card
              className="white-bg no-box-shadow main-card"
              sx={{ borderRadius: 4, padding: 0, overflow: "visible" }}
            >
              <CardContent className={`cardContent leads-list-controls${showingContacts ? "" : " leads-table-view"}`} sx={{ pt: 0, overflowX: "visible" }}>
                <TopListHeaderBar
                  title={showingSalesPipeline ? "Sales Pipeline" : "Leads"}
                  searchValue={search}
                  searchPlaceholder="Search..."
                  searchIcon={<Box component="img" src={leadSearchIcon} alt="" />}
                  onSearchChange={(e) => {
                    setSearch(e.target.value);
                    if (showingContacts) setContactTablePage(1);
                    else setLeadsPage(1);
                  }}
                  actions={topBarActions}
                  tabContent={
                    <Box
                      sx={{
                        px: 0,
                        borderBottom: "1px solid #E5E7EB",
                  }}
                >
                  <TabNavigationBar
                    minHeight={40}
                    indicatorHeight={2}
                    activeColor="#3451F4"
                    labelSx={{ color: "inherit" }}
                    countSx={{ fontSize: 12, lineHeight: "18px", height: 22, px: 1 }}
                    wrapperSx={{ mb: 0 }}
                    value={listTab}
                    onChange={handleListTabChange}
                    items={[
                      { value: LIST_TABS.leads, label: "Leads", count: leadsTotal },
                      {
                        value: LIST_TABS.salesPipeline,
                        label: "Sales Pipeline",
                        count: leadsTotal,
                      },
                      {
                        value: LIST_TABS.contacts,
                        label: CONTACTS_TAB_LABEL,
                        count: contactRowsTotal ?? enrichedContactRows.length,
                      },
                    ]}
                    borderBottom={false}
                  />
                </Box>
                  }
                  resultsToolbarTargetId="leads-results-toolbar"
                />

                <GenericDataTable
                  columns={
                    showingContacts
                      ? headCellsContacts
                      : showingSalesPipeline
                        ? headCellsSalesPipeline
                        : headCellsLeads
                  }
                  exportFields={[...headCellsLeads, ...headCellsSalesPipeline, ...headCellsContacts, ...LEAD_EXPORT_TAB_FIELDS].filter((field) => field.id !== "actions")}
                  rows={filteredRows}
                  renderRow={showingContacts ? renderContactRow : renderLeadRow}
                  defaultOrderBy={showingContacts ? "updated_at" : "last_activity_at"}
                  defaultOrderDirection="desc"
                  resetPageSignal={tableResetSignal}
                  includeSelectionColumn={true}
                  selectionColumnWidth={60}
                  visualVariant="company-standard"
                  fitToContainer
                  resultsToolbarTargetId="leads-results-toolbar"
                  initialRowsPerPage={10}
                  serverSidePagination={
                    contactsUseServerPagination ||
                    (!showingContacts && !advancedApplied && !hasLocalLeadFilters)
                  }
                  resultsTotalCount={showingContacts ? contactTableTotal : leadTableTotal}
                  serverPage={showingContacts ? contactTablePage - 1 : leadsPage - 1}
                  serverRowsPerPage={showingContacts ? contactTableRowsPerPage : leadsRowsPerPage}
                  onServerPageChange={
                    showingContacts ? handleContactTablePageChange : handleLeadsPageChange
                  }
                  onServerRowsPerPageChange={
                    showingContacts
                      ? handleContactTableRowsPerPageChange
                      : handleLeadsRowsPerPageChange
                  }
                  showResultsHeader={true}
                  resultsFilterPills={showingContacts ? contactFilterPills(contactFilters, next => { setContactFilters(next); setContactTablePage(1); }) : resultsFilterPills}
                  bulkActionsTop={showingContacts ? contactBulkNode : bulkNode}
                  bulkActionsBottom={showingContacts ? contactBulkNode : bulkNode}
                  tableContainerSx={
                    showingContacts
                      ? undefined
                      : {
                          "& .MuiTableCell-root": {
                            px: 2,
                          },
                        }
                  }
                  selectedIds={showingContacts ? selectedContactIds : selectedLeadIds}
                  onSelectedIdsChange={(selection) => {
                    const ids = (selection || [])
                      .map((x) => (x && typeof x === "object" ? x.id : x))
                      .filter((v) => v !== undefined && v !== null && v !== "")
                      .map((v) => String(v))
                      .filter(Boolean);

                    if (showingContacts) {
                      setSelectedContactIds(ids);
                      return;
                    }

                    setSelectedLeadIds(
                      ids
                        .map((value) => Number(value))
                        .filter((value) => Number.isFinite(value) && value > 0)
                    );
                  }}
                />
              </CardContent>
            </Card>
          }
        />
      )}

      <CompanyFiltersDrawer
        open={isFilterOpen && !showingContacts}
        onClose={() => setIsFilterOpen(false)}
        rows={rows}
        endpoint={`/api/leads?${new URLSearchParams({ search: search.trim(), stats_filter: activeStatId || "total" })}`}
        ownerLabel={showingSalesPipeline ? "Team Member" : "Owner"}
        value={filtersDraft}
        onChange={setFiltersDraft}
        onClear={clearFilters}
        onApply={applyFilters}
        onAdvancedSearch={openAdvancedSearch}
      />

      <ContactFiltersDrawer open={isFilterOpen && showingContacts} value={contactFilters}
        onClose={() => setIsFilterOpen(false)}
        onApply={next => { setContactFilters(next); setContactTablePage(1); }} />

      <LeadDescriptionDialog
        open={isAddLeadOpen}
        onClose={handleCloseLeadDialog}
        value={leadForm}
        onChange={setLeadForm}
        initialScreen={leadDialogInitialScreen}
        titleOverride={leadDialogTitleOverride}
        submitLabel={leadDialogSubmitLabel}
        onSubmit={handleLeadDialogSubmit}
      />

      <CsvImportDialog
        open={isCsvImportOpen}
        onClose={() => setIsCsvImportOpen(false)}
        onImport={showingContacts ? undefined : handleImportLeads}
        importEndpoint={showingContacts ? `${API_BASE}/api/csv-import/contacts` : ""}
        authHeaders={authHeaders}
        onImported={() => setReloadKey(value => value + 1)}
        entityLabel={showingContacts ? "contacts" : "leads"}
      />

      <AddToSequenceDialog
        open={isAddToSequenceOpen}
        onClose={() => setIsAddToSequenceOpen(false)}
        entityType={showingContacts ? selectedContactSequenceItems[0]?.entityType || "lead" : "lead"}
        items={showingContacts ? selectedContactSequenceItems : selectedSequenceItems}
        user={user}
        onCreated={() => {
          if (showingContacts) {
            setSelectedContactIds([]);
            return;
          }
          handleLeadSequenceCreated();
        }}
      />

      <AIAgentDialog
        open={isAgentDialogOpen}
        onClose={handleCloseAgentDialog}
        user={user}
        initialPrompt={agentDialogInitialPrompt}
        submitOnOpen={agentDialogSubmitOnOpen}
      />

      <LeadProfileDrawer
        open={Boolean(selectedLeadPreview)}
        onClose={closeLeadPreview}
        lead={selectedLeadPreview}
        onShowMore={handleShowMoreLeadPreview}
      />

      <HiringManagerProfileDrawer
        open={Boolean(selectedHiringManager)}
        onClose={closeHiringManagerDrawer}
        manager={selectedHiringManager}
        onShowMore={handleShowMoreHiringManager}
      />

      <CompanyProfileDrawer
        open={Boolean(selectedLeadCompany)}
        onClose={closeLeadCompanyDrawer}
        company={selectedLeadCompany}
        entityType="lead"
        allowShowMore={true}
        onShowMore={handleShowMoreLeadCompany}
      />

      <Menu
        anchorEl={rowMenuAnchorEl}
        open={Boolean(rowMenuAnchorEl)}
        onClose={handleCloseRowMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 180,
            borderRadius: 2.5,
            boxShadow: "0 16px 40px rgba(15,23,42,0.16)",
          },
        }}
      >
        <MenuItem onClick={handleViewLeadAction} sx={{ fontSize: 13 }}>
          View
        </MenuItem>
        <MenuItem onClick={handleShareLeadAction} sx={{ fontSize: 13 }}>
          Share Link
        </MenuItem>
        <MenuItem onClick={handleEditLeadAction} sx={{ fontSize: 13 }}>
          Edit
        </MenuItem>
        <MenuItem onClick={handleDuplicateLeadAction} sx={{ fontSize: 13 }}>
          Duplicate
        </MenuItem>
        <MenuItem onClick={handleArchiveLeadAction} sx={{ fontSize: 13 }}>
          Archive
        </MenuItem>
        {isAdminUser ? (
          <MenuItem onClick={handleDeleteLeadAction} sx={{ fontSize: 13, color: "#B42318" }}>
            Delete
          </MenuItem>
        ) : null}
      </Menu>

      <Menu
        anchorEl={contactMenuAnchorEl}
        open={Boolean(contactMenuAnchorEl)}
        onClose={closeContactMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 180,
            borderRadius: 2.5,
            boxShadow: "0 16px 40px rgba(15,23,42,0.16)",
          },
        }}
      >
        <MenuItem onClick={openContactProfile} sx={{ fontSize: 13 }}>View Profile</MenuItem>
        <MenuItem onClick={shareContact} sx={{ fontSize: 13 }}>Share Link</MenuItem>
        <MenuItem onClick={editContact} sx={{ fontSize: 13 }}>Edit</MenuItem>
        <MenuItem onClick={addContactNote} sx={{ fontSize: 13 }}>Add Notes</MenuItem>
        <MenuItem onClick={addContactTags} sx={{ fontSize: 13 }}>Add Tags</MenuItem>
        <MenuItem onClick={archiveContact} sx={{ fontSize: 13, color: "#B42318" }}>Archive</MenuItem>
      </Menu>

      <Snackbar
        open={actionToast.open}
        autoHideDuration={3200}
        onClose={closeActionToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={closeActionToast}
          severity={actionToast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {actionToast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Leads;
